import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { Suspense, lazy, useEffect, type ReactElement } from 'react';
import { ToastProvider } from './core/components/ui/ToastProvider';
import { Spinner } from './core/components/ui/spinner';
import { ProtectedRoute } from './core/components/auth/protected-route';
import { PermissionRoute } from './core/components/auth/permission-route';
import { DashboardRoute } from './core/components/auth/dashboard-route';
import { RequireFeature } from './core/components/auth/require-feature';
import { RequireRatingMode } from './core/components/auth/require-rating-mode';
// ATL-000381 follow-up — variant-aware module gate for /dispatch-loads/:variant/*.
import { RequireVariantFeature } from './core/components/auth/require-variant-feature';
import { RequireSuperAdmin } from './core/components/auth/require-super-admin';
import { resolveVariantKey } from './pages/dispatch-loads/dispatch-loads-route';
import { DASHBOARD_PATHS, defaultDashboardForRole } from '@tracxtms/shared';
import { SessionExpiryListener } from './core/components/auth/session-expiry-listener';
import { AppBootstrap } from './core/components/bootstrap/app-bootstrap';
import { AppShell } from './core/components/layout/AppShell';
import { LoadRatingPromptModal } from './pages/loads/LoadRatingPromptModal';
import { DatCoverListener } from './pages/dat-loadboard/DatCoverListener';
import { ConfettiCelebration } from './core/components/ui/ConfettiCelebration';
import { RootErrorBoundary } from './core/components/error/RootErrorBoundary';
import { useAuthStore } from './stores/auth-store';
import { useDashboardStore } from './stores/dashboard-store';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

const LoadingFallback = (): ReactElement => (
  <div className="flex items-center justify-center h-screen bg-slate-50">
    <Spinner size="lg" />
  </div>
);

// Public Pages
const LoginPage = lazy(() => import('./pages/login-page'));
// #1775 — Forgot Password (public, self-service).
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password-page'));
const ResetPasswordPage = lazy(() => import('./pages/reset-password-page'));
// WAYP-CUST-08 — Public customer-onboarding landing page. Mounted OUTSIDE
// AuthenticatedLayout so an invited customer can fill in their info
// without a login. Server routes (WAYP-CUST-07) are also unauthenticated.
const CustomerOnboardPublicPage = lazy(
  () => import('./pages/customer-view/OnboardPage'),
);
// #1518 — Public carrier-onboarding landing page. Mounted OUTSIDE
// AuthenticatedLayout (same as the customer one) so an invited carrier can
// set their password + fill in their details without a login. Server routes
// (#1518 carrier-onboarding public router) are also unauthenticated.
const CarrierOnboardPublicPage = lazy(
  () => import('./pages/carrier-view/OnboardPage'),
);
// #1518 — Public sub-driver onboarding landing page. Same pattern as the
// carrier one, one level down (a self-dispatch carrier invites its own
// sub-driver). Mounted OUTSIDE AuthenticatedLayout; server routes
// (#1518 driver-onboarding public router) are also unauthenticated.
const DriverOnboardPublicPage = lazy(
  () => import('./pages/driver-view/OnboardPage'),
);
// Dashboard
const DashboardPage = lazy(() => import('./core/pages/dashboard/DashboardPage'));
const EquipmentUtilizationPage = lazy(() => import('./pages/dashboard/EquipmentUtilizationPage'));
const DriverActivityPage = lazy(() => import('./pages/dashboard/DriverActivityPage'));
const AIOptimizationPage = lazy(() => import('./pages/dashboard/AIOptimizationPage'));
const SLADashboardPage = lazy(() => import('./pages/dashboard/SLADashboardPage'));
const CustomDashboardPage = lazy(() => import('./pages/dashboard/CustomDashboardPage'));
const SharedDashboardPage = lazy(() => import('./pages/dashboard/SharedDashboardPage'));

// App Pages
const DispatchPage = lazy(() => import('./pages/dispatch/DispatchPage'));
const LoadRedirect = lazy(() => import('./pages/loads-shared/LoadRedirect'));
const TripsPage = lazy(() => import('./pages/trips/TripsPage'));
const DriversPage = lazy(() => import('./pages/drivers/DriversPage'));
// ATL-001023 — `/drivers/:id` deep-link resolver (dispatch grid driver links).
const DriverRedirect = lazy(() => import('./pages/drivers/DriverRedirect'));
// WAYP-1179 — Driver Availability Calendar (enterprise feature `driver_availability_calendar`).
const DriverAvailabilityPage = lazy(() => import('./pages/drivers/DriverAvailabilityPage'));
// WAYP-1215 — Fleet-wide Time Off Records (enterprise feature `driver_availability_calendar`).
const TimeOffRecordsPage = lazy(() => import('./pages/drivers/TimeOffRecordsPage'));
// WAYP-611: Supplies sibling module. Placeholder page lives in this branch;
// WAYP-608 lands the real list view and replaces SuppliesPage.tsx on merge.
const SuppliesPage = lazy(() => import('./pages/supplies/SuppliesPage'));
const CustomersPage = lazy(() => import('./pages/customers/CustomersPage'));
// WAYP-952 — V1-parity Rating sub-pages (Customer-category enterprise feature).
const BillingRatesPage = lazy(() => import('./pages/rating/BillingRatesPage'));
const LaneLocationRatesPage = lazy(() => import('./pages/rating/LaneLocationRatesPage'));
const LaneCityRatesPage = lazy(() => import('./pages/rating/LaneCityRatesPage'));
const FscRatesPage = lazy(() => import('./pages/rating/FscRatesPage'));
const AccessorialRatesPage = lazy(() => import('./pages/rating/AccessorialRatesPage'));
const EquipmentPage = lazy(() => import('./pages/equipment/EquipmentPage'));
const LocationsPage = lazy(() => import('./pages/locations/LocationsPage'));
const MapsPage = lazy(() => import('./pages/MapsPage'));
const GeofencePage = lazy(() => import('./pages/maps/GeofencePage'));
const QuotesPage = lazy(() => import('./pages/quotes/QuotesPage'));
const QuoteWizard = lazy(() => import('./pages/quotes/QuoteWizard'));
// ATL-001023 — `/quotes/:id` deep-link resolver (API Tenders "View Quote").
const QuoteRedirect = lazy(() => import('./pages/quotes/QuoteRedirect'));
// WAYP-945 — Dispatch Loads variant-aware surface (Brief §4 + design-spec §0).
// 5 routes per the brief: chooser landing + per-variant list + new + edit +
// detail (read-only fold of the wizard). Component pair lives under
// pages/dispatch-loads/.
const DispatchLoadChooser = lazy(() => import('./pages/dispatch-loads/DispatchLoadChooser'));
const DispatchLoadsPage = lazy(() => import('./pages/dispatch-loads/DispatchLoadsPage'));
const DispatchLoadForm = lazy(() => import('./pages/dispatch-loads/DispatchLoadForm'));
// WAYP-1246 — Loads History (variant-aware Dispatch History grid).
const LoadsHistoryPage = lazy(() => import('./pages/loads-history/LoadsHistoryPage'));
const DispatchTendersPage = lazy(() => import('./pages/dispatch-tenders/DispatchTendersPage'));
// DAT – ATL-001428 (B1): Dispatch → Find Loads (DAT).
const DatFindLoadsPage = lazy(() => import('./pages/dat-loadboard/FindLoadsPage'));
// DAT Margin Calculator: Dispatch → Margin Calculator (DAT).
const DatMarginCalculatorPage = lazy(() => import('./pages/dat-loadboard/MarginCalculatorPage'));
// USPS-CONTRACTS-06 (2026-04-29): the generic `pages/contracts/ContractsPage`
// has been retired. The canonical Contracts module is the USPS-scoped one
// (`pages/usps/ContractsPage` → `UspsContractsPage`) re-homed at
// `/usps/contracts` below (testing feedback 2026-04-30 reverted the path
// from the briefly-shipped `/usps-carrier/contracts` back to the original
// WAYP-177 path; menu placement remains under USPS Carrier nav group).
const InvoicesPage = lazy(() => import('./pages/invoices/InvoicesPage'));
// ATL-001023 — `/invoices/:id` deep-link resolver (Custom Charges invoice links).
const InvoiceRedirect = lazy(() => import('./pages/invoices/InvoiceRedirect'));
const SettlementsPage = lazy(() => import('./pages/settlements/SettlementsPage'));
// #1154 — Deductions & Reimbursements page (3-tab V1 parity rebuild).
const DeductionsPage = lazy(() => import('./pages/deductions/DeductionsPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
// #1573 — landing page for V1 report leaves that haven't been ported yet.
// Duplicates work in PR #1548; rebase will resolve cleanly once #1548 lands.
const ReportComingSoonPage = lazy(() => import('./pages/reports/ReportComingSoonPage'));
// #1641 — Sales by Customer (Detail + Summary) report. Bundles the AG-Grid
// frontend onto PR #1572; backend service + endpoints cherry-picked from
// PR #1548 (`packages/server/src/core/reports/sales-by-customer.service.ts`).
const SalesByCustomerReportPage = lazy(
  () => import('./pages/reports/SalesByCustomerReportPage'),
);
// #1698 — Settlements By Driver/Carrier - Detail ("money out" report).
// Backend service: packages/server/src/core/reports/settlements-driver-carrier-detail.service.ts.
const SettlementsDriverCarrierDetailReportPage = lazy(
  () => import('./pages/reports/SettlementsDriverCarrierDetailReportPage'),
);
// #1722 — Unsettled Loads (working-capital / AR-aging report).
// Backend service: packages/server/src/core/reports/unsettled-loads.service.ts.
const UnsettledLoadsReportPage = lazy(
  () => import('./pages/reports/UnsettledLoadsReportPage'),
);
// #1714 — Equipment Utilization (Detail) report.
// Backend service: packages/server/src/core/reports/equipment-utilization-detail.service.ts.
const EquipmentUtilizationReportPage = lazy(
  () => import('./pages/reports/EquipmentUtilizationReportPage'),
);
// #1657 — Scheduled Trip Miles - Detail (Contractors/USPS Reports).
// Planned-vs-actual mileage variance per scheduled USPS trip; SSRM detail
// grid + recharts contract rollup. V1 source:
// `app/Reports/ScheduledTripMilesDetail/ScheduledTripMilesDetail.php`.
const ScheduledTripMilesDetailReportPage = lazy(
  () => import('./pages/reports/ScheduledTripMilesDetailReportPage'),
);
// V1 SummaryForContractTripDriverLocation + DetailForContractTripDriverLocation —
// USPS on-time service compliance (HCR penalty / contract risk).
// Backend: packages/server/src/core/reports/on-time-appointment.service.ts.
const OnTimeAppointmentReportPage = lazy(
  () => import('./pages/reports/OnTimeAppointmentReportPage'),
);
// #1701 — Completed Loads Detail (Driver/Carrier/Equipment) report + the
// new Billing Backup + audit-trail mechanism. V1 catalog id
// `CompletedDriver` under Dispatch.
const CompletedLoadsDetailReportPage = lazy(
  () => import('./pages/reports/CompletedLoadsDetailReportPage'),
);
// #1691 — Payroll Company Driver Hours and Pay Detail report. Mirrors
// `SalesByCustomerReportPage` (SSRM Detail + per-driver Summary tab +
// hideable chart panel + totals strip).
const DriverHoursPayDetailReportPage = lazy(
  () => import('./pages/reports/DriverHoursPayDetailReportPage'),
);
// V1 ProfitLoad + ProfitCustomer — P&L per load detail and per-customer summary.
// Backend: packages/server/src/core/reports/profit-loss-per-load.service.ts.
// Profit and Loss per Customer Trip Summary (V1 ProfitCustomerTrip) — per-customer
// profit/loss over that customer's TRIP loads.
// Backend: packages/server/src/core/reports/profit-loss-per-customer-trip.service.ts.
// Profit and Loss per Driver Carrier Summary (V1 ProfitDriver) — one row per
// payee, with owner-operators rolled up under their carrier.
// Backend: packages/server/src/core/reports/profit-loss-per-driver-carrier.service.ts.
const ProfitLossPerDriverCarrierReportPage = lazy(
  () => import('./pages/reports/ProfitLossPerDriverCarrierReportPage'),
);
// Contract Rate per Mile (V1 ContractRateperMile, per-contract mode) — one row
// per contract. Note "Rate/Mile" is a mean of stored trip rates, not revenue ÷
// miles; see packages/server/src/core/reports/contract-rate-per-mile.service.ts.
const ContractRatePerMileReportPage = lazy(
  () => import('./pages/reports/ContractRatePerMileReportPage'),
);
// Profit and Loss per Truck, Equipment Summary (V1 ProfitTruck) — one row per
// truck plus an Unassigned bucket.
// Profit and Loss per Trip Detail — V1-parity port of app/Reports/ProfitTrip.
// One row per trip; money summed across the trip's loads.
// Backend: packages/server/src/core/reports/profit-loss-per-trip.service.ts.
const ProfitLossPerTripReportPage = lazy(
  () => import('./pages/reports/ProfitLossPerTripReportPage'),
);
// Backend: packages/server/src/core/reports/profit-loss-per-truck-equipment.service.ts.
const ProfitLossPerTruckEquipmentReportPage = lazy(
  () => import('./pages/reports/ProfitLossPerTruckEquipmentReportPage'),
);
const ProfitLossPerCustomerTripReportPage = lazy(
  () => import('./pages/reports/ProfitLossPerCustomerTripReportPage'),
);
const ProfitLossPerLoadReportPage = lazy(
  () => import('./pages/reports/ProfitLossPerLoadReportPage'),
);
// #2392 — Deductions per Driver/Carrier (V1 DeductionsDriver). One row per
// settlement; deduction types/descriptions concatenated, amounts summed.
// Backend: packages/server/src/core/reports/deductions-per-driver-carrier.service.ts.
const DeductionsPerDriverCarrierReportPage = lazy(
  () => import('./pages/reports/DeductionsPerDriverCarrierReportPage'),
);
// #2427 — Invoiced Work Orders. ONE report replacing V1's two catalog entries
// (InvoicedWorkOrdersDetail + …Summary): Detail = per line item, Summary = per
// work order with the org tax gate applied. NON-UNIT work orders only — the
// complement of Equipment Cost. Despite the name there is no invoice filter.
// Backend: packages/server/src/core/reports/invoiced-work-orders.service.ts.
const InvoicedWorkOrdersReportPage = lazy(
  () => import('./pages/reports/InvoicedWorkOrdersReportPage'),
);

// #2357 — Profit and Loss Summary (V1 ProfitLossSummary). Per-load P&L with
// tax-inclusive revenue; gated columns (FSC / Dead Mile) resolved server-side.
// Backend: packages/server/src/core/reports/profit-loss-summary.service.ts.
const ProfitLossSummaryReportPage = lazy(
  () => import('./pages/reports/ProfitLossSummaryReportPage'),
);
// #2360 — Equipment Cost. ONE report replacing V1's two catalog entries
// (EquipmentCostDetail + EquipmentCostSummary): Detail = per work-order line
// item, Summary = per-equipment rollup carrying both V1 cost bases + variance.
// Backend: packages/server/src/core/reports/equipment-cost.service.ts.
const EquipmentCostReportPage = lazy(
  () => import('./pages/reports/EquipmentCostReportPage'),
);
// #2389 — Driver Sleeper Berth (V1 DriverSleeperBirth). One row per (load,
// driver); Sleeper Berth arrives as HOURS decoded from LoadAssignment's
// epoch+seconds duration column.
// Backend: packages/server/src/core/reports/driver-sleeper-berth.service.ts.
const DriverSleeperBerthReportPage = lazy(
  () => import('./pages/reports/DriverSleeperBerthReportPage'),
);
// ATL-000747 — Driver Shifts: daily work shifts from the Native App's
// start-shift-on-login / End Shift flow.
// Backend: packages/server/src/core/reports/driver-shifts.service.ts.
const DriverShiftsReportPage = lazy(() => import('./pages/reports/DriverShiftsReportPage'));
// Other Cost per Driver/Carrier — V1 OtherCostDriverSummary port (Summary + Detail).
const OtherCostPerDriverCarrierReportPage = lazy(
  () => import('./pages/reports/OtherCostPerDriverCarrierReportPage'),
);
// Other Cost per Equipment — V1 OtherCostEquipmentSummary port (Summary + Detail).
const OtherCostPerEquipmentReportPage = lazy(
  () => import('./pages/reports/OtherCostPerEquipmentReportPage'),
);
// Settlements by Driver/Carrier Summary — V1 SettlementsDriverSummary port.
const SettlementsDriverCarrierSummaryReportPage = lazy(
  () => import('./pages/reports/SettlementsDriverCarrierSummaryReportPage'),
);
// Settlement Payments — ATL-000811. V2-native, NOT a V1 port (see the page's
// own header comment for why).
const SettlementPaymentsReportPage = lazy(
  () => import('./pages/reports/SettlementPaymentsReportPage'),
);
// Reimbursement per Driver/Carrier detail — V1 ReimbursementDriver port.
const ReimbursementsPerDriverCarrierReportPage = lazy(
  () => import('./pages/reports/ReimbursementsPerDriverCarrierReportPage'),
);
// Estimated Miles Report — V1 MilesPerContractTrip port (ATL-000332).
const EstimatedMilesReportPage = lazy(
  () => import('./pages/reports/EstimatedMilesReportPage'),
);
// Extra Trips Report — V1 ExtraTrips port (ATL-000333).
const ExtraTripsReportPage = lazy(() => import('./pages/reports/ExtraTripsReportPage'));
// Miles Report Detail — V1 MilesDetail port (ATL-000335).
const MilesDetailReportPage = lazy(() => import('./pages/reports/MilesDetailReportPage'));
// Missing Start or End Shift — V1 MissingStartEndShift port (ATL-000336).
const MissingShiftReportPage = lazy(() => import('./pages/reports/MissingShiftReportPage'));
// Slips per Driver Summary — V1 SlipsPerDriverSummary port (ATL-000340).
const SlipsPerDriverSummaryReportPage = lazy(
  () => import('./pages/reports/SlipsPerDriverSummaryReportPage'),
);
// Tenders not accepted, New Trips — V1 port (ATL-000343).
const TendersNotAcceptedNewTripsReportPage = lazy(
  () => import('./pages/reports/TendersNotAcceptedNewTripsReportPage'),
);
// Tenders not accepted, Time Changes — V1 port (ATL-001011).
const TendersNotAcceptedTimeChangesReportPage = lazy(
  () => import('./pages/reports/TendersNotAcceptedTimeChangesReportPage'),
);
// Tenders not accepted, Trips not removed — V1 port (ATL-001012).
const TendersNotAcceptedTripsNotRemovedReportPage = lazy(
  () => import('./pages/reports/TendersNotAcceptedTripsNotRemovedReportPage'),
);
const TendersNotAcceptedStopsNotRemovedReportPage = lazy(
  () => import('./pages/reports/TendersNotAcceptedStopsNotRemovedReportPage'),
);
const TendersRejectedReportPage = lazy(
  () => import('./pages/reports/TendersRejectedReportPage'),
);
const TimeOffReportPage = lazy(
  () => import('./pages/reports/TimeOffReportPage'),
);
const DriverExpiriesReportPage = lazy(
  () => import('./pages/reports/DriverExpiriesReportPage'),
);
const EquipmentExpiriesReportPage = lazy(
  () => import('./pages/reports/EquipmentExpiriesReportPage'),
);
// Subscription Details — cross-org subscription listing (ATL-000349).
const SubscriptionDetailsReportPage = lazy(
  () => import('./pages/reports/SubscriptionDetailsReportPage'),
);
// Slips per Driver, Contract, Trip or Bid Detail (ATL-000341).
const SlipsDetailReportPage = lazy(() => import('./pages/reports/SlipsDetailReportPage'));
// Tenders not accepted, New Stops — V1 port (ATL-000342).
const TendersNotAcceptedNewStopsReportPage = lazy(
  () => import('./pages/reports/TendersNotAcceptedNewStopsReportPage'),
);
// Geofence Log Report — V1 GeofenceLogReport port (ATL-000334).
const GeofenceLogReportPage = lazy(() => import('./pages/reports/GeofenceLogReportPage'));
// Quotes — DB-backed quotes report (V1-parity).
// Backend: packages/server/src/core/reports/quotes-report.service.ts.
const QuotesReportPage = lazy(() => import('./pages/reports/QuotesReportPage'));
// Sales by Dispatcher - Detail + per-dispatcher Summary (V1-parity).
// Backend: packages/server/src/core/reports/sales-by-dispatcher-detail.service.ts.
const SalesByDispatcherDetailReportPage = lazy(
  () => import('./pages/reports/SalesByDispatcherDetailReportPage'),
);
// Driver Activity — DB-backed per-driver shift/stop activity log (V1-parity).
// Backend: packages/server/src/core/reports/driver-activity-detail.service.ts.
const DriverActivityReportPage = lazy(
  () => import('./pages/reports/DriverActivityReportPage'),
);
// Notes Report — DB-backed per-note listing (V1-parity).
// Backend: packages/server/src/core/reports/notes-report.service.ts.
const NotesReportPage = lazy(() => import('./pages/reports/NotesReportPage'));
// Completed Loads Detail, Billing Information (V1-parity).
// Backend: packages/server/src/core/reports/completed-loads-billing.service.ts.
const CompletedLoadsBillingReportPage = lazy(
  () => import('./pages/reports/CompletedLoadsBillingReportPage'),
);
// Sales by Equipment - Detail + Summary (V1-parity).
// Backend: packages/server/src/core/reports/sales-by-equipment.service.ts.
const SalesByEquipmentReportPage = lazy(
  () => import('./pages/reports/SalesByEquipmentReportPage'),
);
// Completed Loads Detail — Routing Row Details (per-stop routing rows, V1-parity).
// Backend: packages/server/src/core/reports/completed-loads-routing.service.ts.
const CompletedRoutingReportPage = lazy(
  () => import('./pages/reports/CompletedRoutingReportPage'),
);
// Sales by Rate Type — Summary + Detail (V1-parity).
// Backend: packages/server/src/core/reports/sales-by-rate-type.service.ts.
const SalesByRateTypeReportPage = lazy(
  () => import('./pages/reports/SalesByRateTypeReportPage'),
);
// Open Invoices — DB-backed list of invoices with an outstanding balance (V1-parity).
// Backend: packages/server/src/core/reports/open-invoices.service.ts.
const OpenInvoicesReportPage = lazy(
  () => import('./pages/reports/OpenInvoicesReportPage'),
);
// Uninvoiced Loads — completed loads with no active invoice (V1-parity).
// Backend: packages/server/src/core/reports/uninvoiced-loads.service.ts.
const UninvoicedLoadsReportPage = lazy(
  () => import('./pages/reports/UninvoicedLoadsReportPage'),
);
// Accounts Receivable Aging - Detail + per-customer Summary (V1-parity).
// Backend: packages/server/src/core/reports/accounts-receivable-aging.service.ts.
const AccountsReceivableAgingDetailReportPage = lazy(
  () => import('./pages/reports/AccountsReceivableAgingDetailReportPage'),
);
const DeadheadMilesReportPage = lazy(
  () => import('./pages/reports/DeadheadMilesReportPage'),
);
// Open Loads Detail — Billing Information (V1 OpenBilling; open-loads
// counterpart to /reports/completed-loads-billing).
// Backend: packages/server/src/core/reports/open-loads-billing.service.ts.
const OpenLoadsBillingReportPage = lazy(
  () => import('./pages/reports/OpenLoadsBillingReportPage'),
);
// Open Loads Detail — Driver Carriers and Equipment (V1 OpenDriver; open-loads
// counterpart to /reports/completed-loads-detail).
// Backend: packages/server/src/core/reports/open-loads-detail.service.ts.
const OpenLoadsDetailReportPage = lazy(
  () => import('./pages/reports/OpenLoadsDetailReportPage'),
);
// Open Loads Detail — Routing Row Details (V1 OpenRouting; open-loads
// counterpart to /reports/completed-loads-routing).
// Backend: packages/server/src/core/reports/open-loads-routing.service.ts.
const OpenRoutingReportPage = lazy(
  () => import('./pages/reports/OpenRoutingReportPage'),
);
// Fuel Tax (V1 VrpFuelTax, adapted to V2 ManualFuel — per-state fuel
// summary). Backend: packages/server/src/core/reports/fuel-tax-summary.service.ts.
const FuelTaxSummaryReportPage = lazy(
  () => import('./pages/reports/FuelTaxSummaryReportPage'),
);
// Fuel Tax History (V1 FuelTaxHistory, adapted to V2 ManualFuel —
// per-truck fuel summary). Backend: packages/server/src/core/reports/fuel-tax-history.service.ts.
const FuelTaxHistoryReportPage = lazy(
  () => import('./pages/reports/FuelTaxHistoryReportPage'),
);
// Mileage (V1 VrpMileagesDetail + VrpMileagesSummary, merged into one report
// with Detail / By Truck / By State tabs). Backends:
// packages/server/src/core/reports/mileages-{detail,summary}.service.ts.
const MileageReportPage = lazy(
  () => import('./pages/reports/MileageReportPage'),
);
const SalesCommissionReportPage = lazy(
  () => import('./pages/reports/SalesCommissionReportPage'),
);
const CommissionReportPage = lazy(
  () => import('./pages/reports/CommissionReportPage'),
);
const CrossDockPage = lazy(() => import('./pages/crossdock/CrossDockPage'));
const LivestockPage = lazy(() => import('./pages/livestock/LivestockPage'));
const EDIPage = lazy(() => import('./pages/edi/EDIPage'));
const FourkitesMonitoringPage = lazy(() => import('./pages/edi/FourkitesMonitoringPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const IntegrationApiKeysPage = lazy(() => import('./pages/settings/IntegrationApiKeysPage'));
const DashboardPermissionsPage = lazy(() => import('./pages/settings/DashboardPermissionsPage'));
const CompanyInfoPage             = lazy(() => import('./pages/settings/CompanyInfoPage'));
// WAYP-803: V1 parity port — /settings/company-preferences (General tab + 4 sections;
// per-module tabs are #804–#808).
const CompanyPreferencesPage      = lazy(() => import('./pages/settings/CompanyPreferencesPage'));
const DeductionTypesPage          = lazy(() => import('./pages/settings/DeductionTypesPage'));
const EquipmentTypesPage          = lazy(() => import('./pages/settings/EquipmentTypesPage'));
const DriverRatingQuestionsPage   = lazy(() => import('./pages/settings/DriverRatingQuestionsPage'));
const DriverExpiryTypesPage       = lazy(() => import('./pages/settings/DriverExpiryTypesPage'));
const TimeOffTypesPage            = lazy(() => import('./pages/settings/TimeOffTypesPage'));
const ExpenseTypesPage            = lazy(() => import('./pages/settings/ExpenseTypesPage'));
const DepartmentTypesPage = lazy(() => import('./pages/settings/DepartmentTypesPage'));
const CustomTemplatesPage = lazy(() => import('./pages/settings/CustomTemplatesPage'));
const CarrierRequirementTypesPage = lazy(() => import('./pages/settings/CarrierRequirementTypesPage'));
// WAYP-837 — Location Types settings page (V1 parity).
const LocationTypesPage = lazy(() => import('./pages/settings/LocationTypesPage'));
const TaxesPage = lazy(() => import('./pages/settings/TaxesPage'));
const TradingPartnersPage = lazy(() => import('./pages/settings/TradingPartnersPage'));
const FuelRateSettingsPage = lazy(() => import('./pages/settings/FuelRateSettingsPage'));
const EquipmentExpiriesPage = lazy(() => import('./pages/settings/EquipmentExpiriesPage'));
const TerminalSettingsPage = lazy(() => import('./pages/settings/TerminalSettingsPage'));
// WAYP-INVITE: public accept-invite page. The admin-side invite flow lives
// inline in the existing Settings → Users tab (see SettingsPage.tsx) rather
// than as a separate page.
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
// #1216 USPS Legacy Cleanup (Andrew 2026-05-21): UspsCarrierSettingsPage
// retired alongside its 8 ModuleSetting fields (no V2 readers).
const FileChecklistPage     = lazy(() => import('./pages/settings/FileChecklistPage'));
const RoutingRowTypesPage    = lazy(() => import('./pages/settings/RoutingRowTypesPage'));
const WorkOrderTypesPage     = lazy(() => import('./pages/settings/WorkOrderTypesPage'));
const PurchaseOrderTypesPage = lazy(() => import('./pages/settings/PurchaseOrderTypesPage'));
const LoadCategoriesPage = lazy(() => import('./pages/settings/LoadCategoriesPage'));
// #1790 — Commodity Categories master (gated by the commodity_category feature).
const CommodityCategoriesPage = lazy(() => import('./pages/settings/CommodityCategoriesPage'));
const LoadStatusesPage = lazy(() => import('./pages/settings/LoadStatusesPage'));
const CommissionRulesPage = lazy(() => import('./pages/settings/CommissionRulesPage'));
// WAYP-45 (Round 6, decision.md #40-#43): Custom Dispatch Tabs relocated from
// Company Preferences → standalone page sibling to Load Categories / Load
// Statuses / Routing Row Types under Settings → Loads.
const CustomDispatchTabsPage = lazy(() => import('./pages/settings/CustomDispatchTabsPage'));
// WAYP-681 (umbrella #677) — Frequency Types Settings page. Visibility gate is
// enforced inside the page (USPS module + hide_contracts_and_trips preference);
// PermissionRoute adds the per-module access check.
const FrequencyTypesPage = lazy(() => import('./pages/settings/FrequencyTypesPage'));
const SlipsPage = lazy(() => import('./pages/settings/SlipsPage'));
const CustomDataTypesPage = lazy(() => import('./pages/settings/CustomDataTypesPage'));
const RateServiceTypesPage = lazy(() => import('./pages/settings/RateServiceTypesPage'));
// Weight-Based Zone Rating — Zones master (gated behind weight_based_zone_rating
// at the nav level; server gates every /zones endpoint behind the same feature).
const ZonesPage = lazy(() => import('./pages/settings/ZonesPage'));
// Weight-Based Zone Rating — dedicated full-page weight×zone rate card editor.
const ZoneRateCardPage = lazy(() => import('./pages/settings/ZoneRateCardPage'));
const OtherChargeTypesPage = lazy(() => import('./pages/settings/OtherChargeTypesPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
// #1535 — Notification Center (gated behind notifications_center feature, default ON).
const NotificationCenterPage = lazy(
  () => import('./pages/notifications/NotificationCenterPage'),
);
const FuelRatesPage = lazy(() => import('./pages/fuel/FuelRatesPage'));
const FuelFinderPage = lazy(() => import('./pages/fuel/FuelFinderPage'));
const TruckStopRatingsPage = lazy(() => import('./pages/fuel/TruckStopRatings'));
// WAYP-394 — Manual Fuel page. Gated by the `manual_fuel` enterprise
// feature only (no permission entry — see state/tickets/WAYP-394/
// decisions.md §3).
const ManualFuelPage = lazy(() => import('./pages/fuel/ManualFuelPage'));
const BadgesPage = lazy(() => import('./pages/badges/BadgesPage'));
const DocumentViewer = lazy(() => import('./pages/documents/DocumentViewer'));
const SupportPage = lazy(() => import('./pages/support/SupportPage'));
// RoadmapPage is now embedded as a tab within SupportPage (Platinum Support consolidation)
const DriverAppPage = lazy(() => import('./pages/driver-app/DriverAppPage'));
const CarrierPortalPage = lazy(() => import('./pages/carrier-portal/CarrierPortalPage'));
const CustomerPortalPage = lazy(() => import('./pages/customer-portal/CustomerPortalPage'));
// #1216 USPS Legacy Cleanup (Andrew 2026-05-21): UspsPage retired
// alongside its Vouchers + Invoices tabs. USPS Carrier V2 (PR #1211)
// owns the load lifecycle; the remaining USPS routes live under the
// "USPS Carrier" sidebar group.
// WAYP-505 — placeholder pages for the top-level USPS Carrier nav group.
// Full implementations land in WAYP-506 (Mail Class) and WAYP-507 (Mail Purpose).
const MailClassTypesPage = lazy(() => import('./pages/usps/MailClassTypesPage'));
const MailPurposeTypesPage = lazy(() => import('./pages/usps/MailPurposeTypesPage'));
// WAYP-177 — dedicated routes replace the Contracts + NASS Codes tabs inside UspsPage.
const UspsContractsPage = lazy(() => import('./pages/usps/ContractsPage'));
// WAYP-764 (umbrella) — USPS Trips V1-parity rebuild. Replaces the legacy
// `Operations → USPS → Trips` tab (removed in the same slice). Permission
// scope is the new `trips` module (registered in slice 02). Page lives at
// pages/usps/TripsPage.tsx (slice 5).
const UspsTripsPage = lazy(() => import('./pages/usps/TripsPage'));
// Umbrella #966 (slice f/i) — USPS Recurring Loads page. Owns
// /usps/recurring-loads. Permission scope `recurring:view` (slice b),
// FeatureGate `mod_usps_main` + per-org pref `enableRecurringLoads`
// (slice a) enforced inside the page component.
const RecurringLoadsPage = lazy(() => import('./pages/usps/RecurringLoadsPage'));
// WAYP-973 — Recurring Load create/edit form. Both routes mount the same
// component in different modes; `/new` for create + `/:id/edit` for edit.
// Permission scope `recurring:create` / `recurring:edit` (slice b), gated
// by PermissionRoute below. The component itself doesn't re-run the
// FeatureGate / pref check — the page-level gates apply when navigating
// IN from the page; the form treats the route as a precondition.
const RecurringLoadFormCreatePage = lazy(() =>
  import('./pages/usps/RecurringLoadForm').then((m) => ({
    default: m.RecurringLoadFormCreate,
  })),
);
const RecurringLoadFormEditPage = lazy(() =>
  import('./pages/usps/RecurringLoadForm').then((m) => ({
    default: m.RecurringLoadFormEdit,
  })),
);
// WAYP-927 — Driver Routes workspace (V1 /bids/ → V2 /driver-routes/ port).
// The legacy WAYP-228 BidsPage/BidDetailPage workspace was removed in the
// 2026-05-15 batch — Driver Routes is the V2 replacement.
const DriverRoutesPage = lazy(() => import('./pages/driver-routes/DriverRoutesPage'));
const DriverRouteDetailPage = lazy(() => import('./pages/driver-routes/DriverRouteDetailPage'));
// #1141 (USPS Carrier Loads V2 umbrella) — new USPS Loads page at
// /usps/loads. Slice #1144 ships the Weekly Rolling Window header;
// slices #1145 (Unassigned Trips) + #1146 (Loads grid) slot into the
// same page container. Permission scope `usps_loads:view` (registered
// in slice #1142). Sidebar entry under USPS Carrier nav group.
// #2302 — /usps/loads now redirects to the Planner, so the grid page is no
// longer routed. The page component is intentionally left in the tree (the
// USPS Load form and several modals still import from it); only the lazy
// route binding is retired. Restore this line to bring the route back.
// const UspsLoadsPage = lazy(() => import('./pages/usps/loads/UspsLoadsPage'));
// #2302 — USPS Planner (dedicated USPS clone of the Dispatch Planner).
const UspsPlannerPage = lazy(() => import('./pages/usps-planner/UspsPlannerPage'));
// USPS 5500 (#1556) — slip-reason inquiry grid at /usps/5500. Permission
// scope `usps_5500:view`. Sidebar entry under USPS Carrier > Loads, gated
// additionally on the `organization.usps500` slip-reason preference.
const Usps5500Page = lazy(() => import('./pages/usps/usps-5500/Usps5500Page'));
// USPS Renewal (#1922) — Renewals cycle page at /usps/renewals. Permission
// scope `usps_renewals:view`. Sidebar entry under USPS Carrier, gated on
// `mod_usps_main`.
const RenewalsPage = lazy(() => import('./pages/usps/renewals/RenewalsPage'));
// Reconcile Slips (#1564) — SSRM grid at /usps/reconcile-slips. Permission
// scope `reconcile_slips:view`. Sidebar entry under USPS Carrier > USPS
// Reconciliation, gated additionally on the `organization.usps500` preference.
const ReconcileSlipsPage = lazy(() => import('./pages/usps/reconcile-slips/ReconcileSlipsPage'));
// CLEAR Rates (#1652) — SSRM grid at /usps/clear-rates. Permission scope
// `clear_rates:view`. Sidebar entry under USPS Carrier > USPS Reconciliation,
// gated additionally on the `mod_usps_main` feature + `organization.usps500`
// slip-reason preference (same third gate as USPS 5500).
const ClearRatesPage = lazy(() => import('./pages/usps/clear-rates/ClearRatesPage'));
// FourKites weekly USPS compliance scores at /usps/compliance-scores. Carriers
// see their own weekly score (scope `fourkites_compliance`); super_admins upload
// the weekly file + review the cross-org grid. Gated on the `mod_usps_main` feature.
const ComplianceScoresPage = lazy(() => import('./pages/usps/compliance-scores/ComplianceScoresPage'));
// #1763 — USPS Voucher Import. SSRM grids at /usps/vouchers. Permission scope
// `usps_vouchers:view`. Sidebar entry under USPS Carrier > USPS Reconciliation,
// next to CLEAR Rates, with the same `mod_usps_main` + `usps500` gates.
const VoucherImportPage = lazy(() => import('./pages/usps/vouchers/VoucherImportPage'));
// #1770 — USPS Reconciliation 3-tab page at /usps/reconciliation. Permission
// scope `usps_reconciliation:view`. Sidebar entry under USPS Carrier > USPS
// Reconciliation, next to Voucher Import, with the same gates.
const ReconciliationPage = lazy(() => import('./pages/usps/reconciliation/ReconciliationPage'));
// #1770 — No Miles Reconciliation: its own page/menu (V1 parity). 2 tabs +
// export. Same `usps_reconciliation:view` scope + USPS Reconciliation nav gates.
const NoMilesReconciliationPage = lazy(() => import('./pages/usps/reconciliation/NoMilesReconciliationPage'));
// #1147 — USPS Load form (Create / Edit). Phase 2 of the umbrella.
// New codepath (NOT dispatch-loads/freight). Two routes:
//   /usps/loads/new       — `usps_loads:create`
//   /usps/loads/:id/edit  — `usps_loads:edit`
const UspsLoadFormCreatePage = lazy(
  () => import('./pages/usps/loads/form/UspsLoadFormCreatePage'),
);
const UspsLoadFormEditPage = lazy(
  () => import('./pages/usps/loads/form/UspsLoadFormEditPage'),
);
// #1288 — USPS Load History page (read-only Completed-loads grid).
// Reuses LoadsSection in `variant="history"` mode. Same
// `usps_loads:view` perm; nav entry gated on `mod_usps_main` in
// Sidebar.tsx (route stays accessible to direct URLs for consistency
// with /usps/loads / /usps/trips).
// #2302 — retired alongside the grid route; see the note above.
// const UspsLoadHistoryPage = lazy(
//   () => import('./pages/usps/loads/UspsLoadHistoryPage'),
// );
// USPS Tenders V2 — quotes-based (isTender=true, module='usps') tender queue.
// Replaces the WAYP-231 UspsEdiTender-based queue at the same /usps/tenders route.
const UspsTendersPage = lazy(() => import('./pages/usps/tenders/UspsTendersPage'));
// Process EDI (#1755) — USPS EDI exception-review console at /usps/process-edi.
// Permission scope `usps_process_edi:view`. Nav entry under USPS Carrier > Types,
// gated additionally on the `edi` enterprise feature.
const ProcessEdiPage = lazy(() => import('./pages/usps/process-edi/ProcessEdiPage'));
// #1635 — FarEye inbound API Tenders queue + history + detail (sibling of USPS tenders)
const FareyeTenderQueuePage = lazy(() => import('./pages/fareye/tenders/FareyeTenderQueuePage'));
const FareyeTenderHistoryPage = lazy(() => import('./pages/fareye/tenders/FareyeTenderHistoryPage'));
const FareyeTenderDetailPage = lazy(() => import('./pages/fareye/tenders/FareyeTenderDetailPage'));
// ATL-001415 — AI Email Intake (review queue + email detail + mailbox settings).
const EmailIntakeQueuePage = lazy(() => import('./pages/email-intake/EmailIntakeQueuePage'));
const EmailIntakeDetailPage = lazy(() => import('./pages/email-intake/EmailIntakeDetailPage'));
const EmailIntakeSettingsPage = lazy(() => import('./pages/email-intake/EmailIntakeSettingsPage'));
const LegacyEmailIntakeSettingsRedirect = lazy(() =>
  import('./pages/email-intake/email-intake-ui').then((m) => ({ default: m.LegacySettingsRedirect })),
);
const ReeferPage = lazy(() => import('./pages/reefer/ReeferPage'));
const FreightPage = lazy(() => import('./pages/freight/FreightPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const EscrowPage = lazy(() => import('./pages/financial/EscrowPage'));
const DriverLoansPage = lazy(() => import('./pages/financial/DriverLoansPage'));
const FuelCardsPage = lazy(() => import('./pages/financial/FuelCardsPage'));
// #1747 — EFS / fuel-card import triage page (transactions / missing refs / overrides).
const EfsFuelCardsPage = lazy(() => import('./pages/financial/EfsFuelCardsPage'));
const WorkOrdersPage = lazy(() => import('./pages/financial/WorkOrdersPage'));
// WAYP-389: Purchase Orders list page (V2 port of V1 /purchase-order).
const PurchaseOrdersPage = lazy(() => import('./pages/financial/PurchaseOrdersPage'));
const ReportCatalogPage = lazy(() => import('./pages/financial/ReportCatalogPage'));
const ReceivedPaymentsPage = lazy(() => import('./pages/financial/ReceivedPaymentsPage'));
// WAYP-392: Expenses module (V1 parity port)
const ExpensesPage = lazy(() => import('./pages/financial/ExpensesPage'));
// #1087: Custom Charges / Credits — Finance menu V1→V2 foundation.
const CustomChargesPage = lazy(() => import('./pages/financial/CustomChargesPage'));
// Other Charges or Reimbursements — V1 Invoicing list page parity.
const OtherChargesReimbursementsPage = lazy(() => import('./pages/financial/OtherChargesReimbursementsPage'));
// #1137: Customer Statements & Collections — V1-parity port (Finance menu).
const CustomerStatementsPage = lazy(() => import('./pages/customer-statements/CustomerStatementsPage'));
const JobQueuePage = lazy(() => import('./pages/admin/JobQueuePage'));
const GpsDeviceAdminPage = lazy(() => import('./pages/admin/GpsDeviceAdminPage'));
const FeatureManagementPage = lazy(() => import('./pages/admin/FeatureManagementPage'));
const IntegrationAdminPage = lazy(() => import('./pages/admin/IntegrationAdminPage'));
const OnboardCustomerPage = lazy(() => import('./pages/admin/OnboardCustomerPage'));
const SwitchUserPage = lazy(() => import('./pages/admin/SwitchUserPage'));
// #1225 slice 7 — Scheduled Commands page (super-admin only).
const CommandsPage = lazy(() => import('./pages/admin/CommandsPage'));

// WAYP-311: Multi-currency & cross-border
const ExchangeRatesPage = lazy(() => import('./pages/international/ExchangeRatesPage'));
const TaxRatesPage = lazy(() => import('./pages/international/TaxRatesPage'));
// WAYP-395: V2 Customs Brokers page lives at `pages/freight/CustomsBrokersPage`.
// The legacy `pages/international/CustomsBrokersPage` was removed once the
// new module covered every flow it served (master-data screen + future
// load-attached customs-info dropdown will consume `useCustomsBrokers`
// from `hooks/use-customs-brokers.ts` against `/api/v1/broker`).
const CustomsBrokersPage = lazy(() => import('./pages/freight/CustomsBrokersPage'));

// WAYP-319: QuickBooks Desktop Integration
const QbdIntegrationPage = lazy(() => import('./pages/settings/QbdIntegrationPage'));
const QuickBooksOnlinePage = lazy(() => import('./pages/settings/QuickBooksOnlinePage'));
// #1731 — Google Calendar integration settings (per-user connect/disconnect).
const GoogleCalendarPage = lazy(() => import('./pages/settings/GoogleCalendarPage'));
// #1740 — Outlook Calendar integration settings (per-user connect/disconnect).
const OutlookCalendarPage = lazy(() => import('./pages/settings/OutlookCalendarPage'));

// WAYP-326: FreightBid AI — Phase 4 Frontend
const FreightBidScenariosPage = lazy(() => import('./pages/freightbid/FreightBidScenariosPage'));

// Audit Log
const AuditLogPage = lazy(() => import('./pages/audit-logs/AuditLogPage'));
const FreightBidScenarioDetailPage = lazy(() => import('./pages/freightbid/FreightBidScenarioDetailPage'));
const FreightBidComparePage = lazy(() => import('./pages/freightbid/FreightBidComparePage'));

// Tracx admin (separate /admin/* shell — super_admin only)
const PlatformAdminLayout = lazy(() =>
  import('./pages/platform/PlatformAdminLayout').then((m) => ({ default: m.PlatformAdminLayout })),
);
const PlatformDashboardPage = lazy(() =>
  import('./pages/platform/PlatformDashboardPage').then((m) => ({ default: m.PlatformDashboardPage })),
);
const PlatformOrganizationsPage = lazy(() =>
  import('./pages/platform/PlatformOrganizationsPage').then((m) => ({ default: m.PlatformOrganizationsPage })),
);
const PlatformBannersPage = lazy(() =>
  import('./pages/platform/PlatformBannersPage').then((m) => ({ default: m.PlatformBannersPage })),
);
const PlatformOrganizationDetailPage = lazy(() =>
  import('./pages/platform/PlatformOrganizationDetailPage').then((m) => ({
    default: m.PlatformOrganizationDetailPage,
  })),
);
const PlatformSystemHealthPage = lazy(() =>
  import('./pages/platform/PlatformSystemHealthPage').then((m) => ({
    default: m.PlatformSystemHealthPage,
  })),
);
const PlatformUsersPage = lazy(() =>
  import('./pages/platform/PlatformUsersPage').then((m) => ({
    default: m.PlatformUsersPage,
  })),
);
const PlatformAuditLogPage = lazy(() =>
  import('./pages/platform/PlatformAuditLogPage').then((m) => ({
    default: m.PlatformAuditLogPage,
  })),
);
const PlatformAiUsagePage = lazy(() =>
  import('./pages/platform/PlatformAiUsagePage').then((m) => ({
    default: m.PlatformAiUsagePage,
  })),
);
const PlatformOrgHealthPage = lazy(() =>
  import('./pages/platform/PlatformOrgHealthPage').then((m) => ({
    default: m.PlatformOrgHealthPage,
  })),
);
const PlatformFeatureAdoptionPage = lazy(() =>
  import('./pages/platform/PlatformFeatureAdoptionPage').then((m) => ({
    default: m.PlatformFeatureAdoptionPage,
  })),
);
const PlatformFeatureFlagsPage = lazy(() =>
  import('./pages/platform/PlatformFeatureFlagsPage').then((m) => ({
    default: m.PlatformFeatureFlagsPage,
  })),
);
const PlatformIntegrationsPage = lazy(() =>
  import('./pages/platform/PlatformIntegrationsPage').then((m) => ({
    default: m.PlatformIntegrationsPage,
  })),
);
const PlatformApiClientsPage = lazy(() =>
  import('./pages/platform/PlatformApiClientsPage').then((m) => ({
    default: m.PlatformApiClientsPage,
  })),
);
const PlatformSupportPage = lazy(() =>
  import('./pages/platform/PlatformSupportPage').then((m) => ({
    default: m.PlatformSupportPage,
  })),
);
const PlatformActivityStreamPage = lazy(() =>
  import('./pages/platform/PlatformActivityStreamPage').then((m) => ({
    default: m.PlatformActivityStreamPage,
  })),
);
const PlatformSubscriptionsPage = lazy(() =>
  import('./pages/platform/PlatformSubscriptionsPage').then((m) => ({
    default: m.PlatformSubscriptionsPage,
  })),
);
const PlatformCostAttributionPage = lazy(() =>
  import('./pages/platform/PlatformCostAttributionPage').then((m) => ({
    default: m.PlatformCostAttributionPage,
  })),
);
const PlatformBillingAnomaliesPage = lazy(() =>
  import('./pages/platform/PlatformBillingAnomaliesPage').then((m) => ({
    default: m.PlatformBillingAnomaliesPage,
  })),
);

// Root path: send authenticated users to the dashboard, otherwise to login.
// Hitting `/` while already signed in must not bounce back to /login.
function RootRedirect(): ReactElement {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const destination = isAuthenticated ? DASHBOARD_PATHS[defaultDashboardForRole(role)] : '/login';
  return <Navigate to={destination} replace />;
}

// Layout wrapper that combines ProtectedRoute + AppBootstrap + AppShell.
// AppBootstrap gates rendering until User + Org + EnterpriseSettings are
// loaded, so no page below this ever sees a half-hydrated state.
function AuthenticatedLayout(): ReactElement {
  const userId = useAuthStore((s) => s.user?.id);
  const initDashboards = useDashboardStore((s) => s.initialize);
  useEffect(() => {
    initDashboards(userId);
  }, [userId, initDashboards]);
  return (
    <ProtectedRoute>
      <AppBootstrap>
        <AppShell>
          <Outlet />
        </AppShell>
        {/* Driver Carrier Performance (V1 parity): global load-completion rating
            dialog — opens whenever a load is marked Completed (any grid). */}
        <LoadRatingPromptModal />
        {/* DAT - ATL-001438 (C5): toast / badge refresh when a load action
            covers or closes a load that is posted on DAT. */}
        <DatCoverListener />
      </AppBootstrap>
    </ProtectedRoute>
  );
}

/**
 * 2026-07-29 — The standalone General Freight loads list is subsumed by the
 * Dispatch Planner (Weekly Rolling view), which owns the view-mode switcher.
 * The nav link was removed, so the base freight list URL is reachable only by
 * direct link / refresh — redirect it to the planner so operators always land
 * on the full surface. Other variants (no planner yet) keep their standalone
 * list; the freight /new, /:id/edit, /recurring sub-routes are untouched.
 */
function DispatchLoadsListOrPlannerRedirect(): ReactElement {
  const { variant } = useParams<{ variant: string }>();
  if (resolveVariantKey(variant ?? '') === 'mod_dispatch_freight_main') {
    return <Navigate to="/dispatch" replace />;
  }
  return <DispatchLoadsPage />;
}

export default function App(): ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfettiCelebration />
        <Router>
          <SessionExpiryListener>
            <RootErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public Routes — `/` routes based on session: authenticated → /dashboard, else /login. */}
                <Route path="/" element={<RootRedirect />} />
                <Route path="/login" element={<LoginPage />} />
                {/* WAYP-INVITE: token-bearing accept page is public — the token is the auth factor. */}
                <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
                {/* WAYP-CUST-08 — Public customer onboarding landing page.
                    Reached by the invitation link the admin sends; matches
                    the V1 URL shape `/customer-view/onboard/<code>`. No
                    AuthenticatedLayout wrapper — the page renders without
                    a logged-in session. */}
                <Route
                  path="/customer-view/onboard/:code"
                  element={<CustomerOnboardPublicPage />}
                />
                {/* #1518 — Public carrier onboarding landing page. Same
                    pattern as the customer one; matches the V1 URL shape
                    `/carrier-view/onboard/<code>`. No AuthenticatedLayout. */}
                <Route
                  path="/carrier-view/onboard/:code"
                  element={<CarrierOnboardPublicPage />}
                />
                {/* #1518 — Public sub-driver onboarding landing page. Reached
                    by the invite link a self-dispatch carrier sends its own
                    sub-driver; matches the V2 URL shape
                    `/driver-view/onboard/<code>`. No AuthenticatedLayout. */}
                <Route
                  path="/driver-view/onboard/:code"
                  element={<DriverOnboardPublicPage />}
                />
                {/* #1775 — Forgot Password flow (public, no AuthenticatedLayout). */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                {/* Protected Routes — all wrapped in AppShell */}
                <Route element={<AuthenticatedLayout />}>
                  {/* Dashboard — accessible to anyone who can view loads */}
                  <Route path="/dashboard" element={<DashboardRoute dashboardId="main"><DashboardPage /></DashboardRoute>} />
                  <Route path="/dashboard/equipment-utilization" element={<DashboardRoute dashboardId="equipment-utilization"><PermissionRoute module="equipment"><EquipmentUtilizationPage /></PermissionRoute></DashboardRoute>} />
                  {/* Driver Activity dashboard (built-in scorecard canvas, sibling
                      of the other /dashboard/* built-ins). The DB-backed Driver
                      Activity *report* lives separately at /reports/driver-activity
                      and is reached from the Reports menu — they are distinct
                      surfaces and both are kept. */}
                  <Route path="/dashboard/driver-activity" element={<DashboardRoute dashboardId="driver-activity"><PermissionRoute module="drivers"><DriverActivityPage /></PermissionRoute></DashboardRoute>} />
                  <Route path="/dashboard/ai-optimization" element={<DashboardRoute dashboardId="ai-optimization"><PermissionRoute module="ai"><AIOptimizationPage /></PermissionRoute></DashboardRoute>} />
                  <Route path="/dashboard/sla" element={<DashboardRoute dashboardId="sla"><PermissionRoute module="reports"><SLADashboardPage /></PermissionRoute></DashboardRoute>} />
                  {/* FourKites compliance under the Dashboard menu — always the
                      carrier-facing view (variant="customer"), so it shows exactly
                      what a carrier sees. Same gates as /usps/compliance-scores. */}
                  <Route path="/dashboard/compliance-scores" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="fourkites_compliance"><ComplianceScoresPage variant="customer" /></PermissionRoute></RequireFeature>} />
                  <Route path="/dashboard/custom/:id" element={<DashboardRoute dashboardId="main"><CustomDashboardPage /></DashboardRoute>} />
                  <Route path="/dashboard/shared/:id" element={<SharedDashboardPage />} />

                  {/* Dispatch — requires dispatch.view.
                      ATL-000381 follow-up: module routes are now feature-gated at the
                      route level (RequireFeature → /dashboard when the module is OFF
                      for the active org), so a login deep-link restore or an org
                      switch onto a module URL can't land on a broken page. */}
                  <Route path="/dispatch" element={<RequireFeature featureKey="mod_dispatch_freight_main"><PermissionRoute module="dispatch"><DispatchPage /></PermissionRoute></RequireFeature>} />
                  {/* DAT – ATL-001428 (B1): search the DAT load board (feature-gated). */}
                  <Route path="/dispatch/dat/find-loads" element={<RequireFeature featureKey="dat_loadboard"><PermissionRoute module="loads"><DatFindLoadsPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/dispatch/dat/margin-calculator" element={<RequireFeature featureKey="dat_loadboard"><PermissionRoute module="loads"><DatMarginCalculatorPage /></PermissionRoute></RequireFeature>} />

                  {/* #1887 — legacy generic /loads pages removed; loads live on the
                      per-module surfaces (/dispatch-loads/:variant, /usps/loads).
                      `/loads/:id` is kept ONLY as a module resolver: id-only deep
                      links (audit/notifications, dispatch planner) redirect to the
                      load's correct module form. */}
                  <Route path="/loads/:id" element={<PermissionRoute module="loads"><LoadRedirect /></PermissionRoute>} />
                  <Route path="/trips" element={<PermissionRoute module="loads"><TripsPage /></PermissionRoute>} />

                  {/* People & Assets — unified drivers/carriers grid at /drivers. */}
                  <Route path="/drivers" element={<PermissionRoute module="drivers"><DriversPage /></PermissionRoute>} />
                  {/* WAYP-1179 — Driver Availability Calendar. Gated on `driver_availability_calendar` enterprise feature. */}
                  <Route path="/drivers/availability" element={
                    <RequireFeature featureKey="driver_availability_calendar">
                      <PermissionRoute module="drivers"><DriverAvailabilityPage /></PermissionRoute>
                    </RequireFeature>
                  } />
                  {/* WAYP-1215 — Fleet-wide Time Off Records. Gated on `driver_availability_calendar` enterprise feature. */}
                  <Route path="/drivers/time-off-records" element={
                    <RequireFeature featureKey="driver_availability_calendar">
                      <PermissionRoute module="drivers"><TimeOffRecordsPage /></PermissionRoute>
                    </RequireFeature>
                  } />
                  {/* ATL-001023 — `/drivers/:id` deep-link resolver. Dispatch grids
                      link the Driver/Carrier name here; it resolves the id and
                      redirects to the Fleet grid pre-searched on the driver name.
                      Placed after the static /drivers/* routes above. */}
                  <Route path="/drivers/:id" element={<PermissionRoute module="drivers"><DriverRedirect /></PermissionRoute>} />
                  {/* WAYP-611: Supplies sibling module — vendor master, gated on `supplies:view`. */}
                  <Route path="/supplies" element={<PermissionRoute module="supplies"><SuppliesPage /></PermissionRoute>} />
                  {/* WAYP-511: reconcile to `customers` permission key (was `carriers`). */}
                  <Route path="/customers" element={<PermissionRoute module="customers"><CustomersPage /></PermissionRoute>} />
                  {/* WAYP-952 — V1-parity Rating sub-pages, all gated by `customer:rating`
                      enterprise feature + per-page RBAC scope. RequireFeature wraps
                      each route so the page renders null when the org has the
                      Customer Rating feature OFF (mirrors NAV_VALUE_TO_FEATURE_KEY). */}
                  <Route path="/rating/billing-rates" element={<RequireFeature featureKey="customer:rating"><PermissionRoute module="billing_rates"><BillingRatesPage /></PermissionRoute></RequireFeature>} />
                  {/* WAYP-952 — Lane-mode variants of the main Rating page.
                      Sidebar shows exactly one of these three based on
                      `customer:rating.configJson.rate_type`. Routes stay
                      registered so a deep-linked URL still resolves if the
                      user flips the preference mid-session, but
                      ATL-001214 (finding #5) adds RequireRatingMode so a
                      mode-inappropriate URL redirects instead of rendering
                      a fully-functional page (V1 never exposes these
                      outside the chosen mode, by any path). */}
                  <Route path="/rating/lane-location-rates" element={<RequireFeature featureKey="customer:rating"><RequireRatingMode mode="Lanes/Locations"><PermissionRoute module="billing_rates"><LaneLocationRatesPage /></PermissionRoute></RequireRatingMode></RequireFeature>} />
                  <Route path="/rating/lane-city-rates" element={<RequireFeature featureKey="customer:rating"><RequireRatingMode mode="Lanes/Cities"><PermissionRoute module="billing_rates"><LaneCityRatesPage /></PermissionRoute></RequireRatingMode></RequireFeature>} />
                  <Route path="/rating/fsc-rates" element={<RequireFeature featureKey="customer:rating"><PermissionRoute module="fsc_rates"><FscRatesPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/rating/accessorial-rates" element={<RequireFeature featureKey="customer:rating"><PermissionRoute module="accessorial_rates"><AccessorialRatesPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/equipment" element={<PermissionRoute module="equipment"><EquipmentPage /></PermissionRoute>} />
                  <Route path="/locations" element={<PermissionRoute module="locations"><LocationsPage /></PermissionRoute>} />

                  {/* Maps */}
                  <Route path="/maps" element={<PermissionRoute module="maps"><MapsPage /></PermissionRoute>} />
                  <Route path="/maps/geofences" element={<PermissionRoute module="maps"><GeofencePage /></PermissionRoute>} />

                  {/* Fuel */}
                  <Route path="/fuel" element={<PermissionRoute module="fuel"><FuelRatesPage /></PermissionRoute>} />
                  <Route path="/fuel/finder" element={<PermissionRoute module="fuel"><FuelFinderPage /></PermissionRoute>} />
                  <Route path="/fuel/ratings" element={<PermissionRoute module="fuel"><TruckStopRatingsPage /></PermissionRoute>} />
                  {/* WAYP-394 — Manual Fuel. Feature-flag gated only; NO
                      PermissionRoute (decisions.md §3). */}
                  <Route path="/fuel/manual-fuel" element={
                    <RequireFeature featureKey="manual_fuel">
                      <ManualFuelPage />
                    </RequireFeature>
                  } />

                  {/* Quotes — Dispatch surface (Operations → Dispatch dropdown). */}
                  <Route path="/quotes" element={<RequireFeature featureKey="mod_dispatch_freight_main"><PermissionRoute module="quotes"><QuotesPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/quotes/new" element={<RequireFeature featureKey="mod_dispatch_freight_main"><PermissionRoute module="quotes" action="create"><QuoteWizard /></PermissionRoute></RequireFeature>} />
                  <Route path="/quotes/:id/edit" element={<RequireFeature featureKey="mod_dispatch_freight_main"><PermissionRoute module="quotes" action="edit"><QuoteWizard /></PermissionRoute></RequireFeature>} />
                  {/* ATL-001023 — `/quotes/:id` deep-link resolver. API Tenders
                      "View Quote" links here; it resolves the id, picks the
                      quote's module surface, and redirects to that Quotes grid
                      pre-searched on the quote's displayId. */}
                  <Route path="/quotes/:id" element={<RequireFeature featureKey="mod_dispatch_freight_main"><PermissionRoute module="quotes"><QuoteRedirect /></PermissionRoute></RequireFeature>} />
                  {/* WAYP-814 follow-up — USPS Carrier surface for Quotes.
                      Mounts the same `<QuotesPage />` and `<QuoteWizard />`
                      components with `defaultModule="usps"`. The component
                      pair internally resolves the matching long key
                      (`mod_usps_main`) for Rate Type / Fuel Rate / Quotable
                      Load Status filtering and uses `/usps/quotes` as the
                      back-nav base. Permission gate is the same `quotes`
                      module — USPS Carrier doesn't introduce a separate
                      permission scope. Menu entry lives under the USPS
                      Carrier dropdown in Sidebar.tsx. */}
                  <Route path="/usps/quotes" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="quotes"><QuotesPage defaultModule="usps" /></PermissionRoute></RequireFeature>} />
                  <Route path="/usps/quotes/new" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="quotes" action="create"><QuoteWizard defaultModule="usps" /></PermissionRoute></RequireFeature>} />
                  <Route path="/usps/quotes/:id/edit" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="quotes" action="edit"><QuoteWizard defaultModule="usps" /></PermissionRoute></RequireFeature>} />
                  {/* WAYP-814 follow-up — Intermodal Quotes surface. Mounts
                      the same QuotesPage / QuoteWizard pair with
                      `defaultModule="intermodal"`. Menu visibility is
                      gated on the `mod_intermodal_main` enterprise feature
                      in Sidebar.tsx; the routes themselves stay registered
                      regardless so a deep link still works for any user
                      who has `quotes:view`. */}
                  <Route path="/intermodal/quotes" element={<RequireFeature featureKey="mod_intermodal_main"><PermissionRoute module="quotes"><QuotesPage defaultModule="intermodal" /></PermissionRoute></RequireFeature>} />
                  <Route path="/intermodal/quotes/new" element={<RequireFeature featureKey="mod_intermodal_main"><PermissionRoute module="quotes" action="create"><QuoteWizard defaultModule="intermodal" /></PermissionRoute></RequireFeature>} />
                  <Route path="/intermodal/quotes/:id/edit" element={<RequireFeature featureKey="mod_intermodal_main"><PermissionRoute module="quotes" action="edit"><QuoteWizard defaultModule="intermodal" /></PermissionRoute></RequireFeature>} />
                  {/* WAYP-814 follow-up — Full Truckload Brokerage Quotes
                      surface. Sibling of `/freight` (FreightPage). Gated on
                      `mod_ftl_brokerage_main`. */}
                  <Route path="/freight/quotes" element={<RequireFeature featureKey="mod_ftl_brokerage_main"><PermissionRoute module="quotes"><QuotesPage defaultModule="ftl_brokerage" /></PermissionRoute></RequireFeature>} />
                  <Route path="/freight/quotes/new" element={<RequireFeature featureKey="mod_ftl_brokerage_main"><PermissionRoute module="quotes" action="create"><QuoteWizard defaultModule="ftl_brokerage" /></PermissionRoute></RequireFeature>} />
                  <Route path="/freight/quotes/:id/edit" element={<RequireFeature featureKey="mod_ftl_brokerage_main"><PermissionRoute module="quotes" action="edit"><QuoteWizard defaultModule="ftl_brokerage" /></PermissionRoute></RequireFeature>} />
                  {/* WAYP-814 follow-up — Livestock Quotes surface.
                      Sibling of `/livestock` (LivestockPage). Gated on
                      `mod_livestock_main`. */}
                  <Route path="/livestock/quotes" element={<RequireFeature featureKey="mod_livestock_main"><PermissionRoute module="quotes"><QuotesPage defaultModule="livestock" /></PermissionRoute></RequireFeature>} />
                  <Route path="/livestock/quotes/new" element={<RequireFeature featureKey="mod_livestock_main"><PermissionRoute module="quotes" action="create"><QuoteWizard defaultModule="livestock" /></PermissionRoute></RequireFeature>} />
                  <Route path="/livestock/quotes/:id/edit" element={<RequireFeature featureKey="mod_livestock_main"><PermissionRoute module="quotes" action="edit"><QuoteWizard defaultModule="livestock" /></PermissionRoute></RequireFeature>} />
                  {/* WAYP-945 — Dispatch Loads variant-aware surface (Brief §4).
                      5 routes:
                        /dispatch-loads                 → 4-card chooser landing
                        /dispatch-loads/:variant        → per-variant AG-Grid list
                        /dispatch-loads/:variant/new    → 8-step wizard, create mode
                        /dispatch-loads/:variant/:id/edit → 8-step wizard, edit mode
                        /dispatch-loads/:variant/:id    → wizard read-only fold (detail) */}
                  <Route path="/dispatch-loads" element={<PermissionRoute module="loads"><DispatchLoadChooser /></PermissionRoute>} />
                  {/* WAYP-1246 — Loads History (variant-aware Dispatch History
                      grid). Routes MUST be declared BEFORE the catch-all
                      `/dispatch-loads/:variant` below so the static `history`
                      segment doesn't get captured as `:variant`. The shared
                      component reads the variant slug from either the URL
                      param OR `?variant=` query string (defaults to freight). */}
                  <Route path="/dispatch-loads/history" element={<PermissionRoute module="loads" action="history"><LoadsHistoryPage /></PermissionRoute>} />
                  <Route path="/dispatch-loads/history/:variant" element={<RequireVariantFeature><PermissionRoute module="loads" action="history"><LoadsHistoryPage /></PermissionRoute></RequireVariantFeature>} />
                  {/* Dispatch Tenders (V1 Dispatch → EDI Tenders parity). Static
                      `tender` segment MUST precede the `:variant` catch-all so it
                      isn't captured as a variant. The page carries its own
                      EDI / History / API / EBA tab strip. Edit-only — no create. */}
                  <Route path="/dispatch-loads/tender" element={<RequireFeature featureKey="mod_dispatch_freight_main"><PermissionRoute module="loads"><DispatchTendersPage /></PermissionRoute></RequireFeature>} />
                  {/* ATL-000381 follow-up — per-variant module gate: the :variant slug
                      resolves to its mod_*_main feature; disabled module → /dashboard. */}
                  <Route path="/dispatch-loads/:variant" element={<RequireVariantFeature><PermissionRoute module="loads"><DispatchLoadsListOrPlannerRedirect /></PermissionRoute></RequireVariantFeature>} />
                  {/* 2026-05-19 — Recurring Loads surface per variant.
                      Same component as the operational grid; the `mode`
                      prop pins isRecurring=true in the SSRM context and
                      swaps title + empty-state copy. Routes go BEFORE
                      `:variant/new` so the static `recurring` segment
                      doesn't get captured as `:id`. */}
                  <Route path="/dispatch-loads/:variant/recurring" element={<RequireVariantFeature><PermissionRoute module="loads"><DispatchLoadsPage mode="recurring" /></PermissionRoute></RequireVariantFeature>} />
                  <Route path="/dispatch-loads/:variant/new" element={<RequireVariantFeature><PermissionRoute module="loads" action="create"><DispatchLoadForm /></PermissionRoute></RequireVariantFeature>} />
                  <Route path="/dispatch-loads/:variant/:id/edit" element={<RequireVariantFeature><PermissionRoute module="loads" action="edit"><DispatchLoadForm /></PermissionRoute></RequireVariantFeature>} />
                  <Route path="/dispatch-loads/:variant/:id" element={<RequireVariantFeature><PermissionRoute module="loads"><DispatchLoadForm readOnly /></PermissionRoute></RequireVariantFeature>} />
                  {/* Contracts — USPS-CONTRACTS-06 (rebuild brief, 2026-04-29);
                      URL reverted 2026-04-30 per testing feedback.
                      The generic `Contract` module is fully retired in this
                      slice. The canonical Contracts module is now the
                      USPS-scoped `UspsContractsPage`, mounted under USPS
                      Carrier nav at `/usps/contracts` (the original WAYP-177
                      path) and guarded by the dedicated `contracts:view`
                      scope (registered in USPS-CONTRACTS-02). The
                      briefly-shipped `/usps-carrier/contracts` path
                      redirects below so any bookmarks from that window
                      still work. */}

                  {/* Financial — blocked for dispatchers, drivers, carriers */}
                  {/* #1077: permission module renamed `invoicing` → `invoices`. */}
                  <Route path="/invoices" element={<PermissionRoute module="invoices"><InvoicesPage /></PermissionRoute>} />
                  {/* ATL-001023 — `/invoices/:id` deep-link resolver. Custom Charges
                      invoice-number links here; it resolves the id and redirects
                      to the Invoices grid pre-searched on the invoice number. */}
                  <Route path="/invoices/:id" element={<PermissionRoute module="invoices"><InvoiceRedirect /></PermissionRoute>} />
                  <Route path="/settlements" element={<PermissionRoute module="settlements"><SettlementsPage /></PermissionRoute>} />
                  {/* #1154 — Deductions & Reimbursements page. Gated by the
                      same `settlements:view` scope as the Settlements page
                      (V1 also keys both off the Settlements module). */}
                  <Route path="/deductions" element={<PermissionRoute module="settlements"><DeductionsPage /></PermissionRoute>} />
                  {/* #920 — Escrow Accounts V1 parity rebuild — own permission module (6 actions). */}
                  <Route path="/financial/escrow" element={<PermissionRoute module="escrow_accounts"><EscrowPage /></PermissionRoute>} />
                  {/* #898 — Loans uses its own `loans` permission module (V1 parity, 6 granular actions). */}
                  <Route path="/financial/driver-loans" element={<PermissionRoute module="loans"><DriverLoansPage /></PermissionRoute>} />
                  <Route path="/financial/fuel-cards" element={<PermissionRoute module="fuel"><FuelCardsPage /></PermissionRoute>} />
                  <Route path="/financial/fuel-cards-import" element={<PermissionRoute module="fuel"><EfsFuelCardsPage /></PermissionRoute>} />
                  {/* #1077: Work Orders piggybacks on the invoices permission family
                      (it has its own `work_orders` module too; this route uses the
                      broader `invoices:view` gate for finance-area access). */}
                  <Route path="/financial/work-orders" element={<PermissionRoute module="invoices"><WorkOrdersPage /></PermissionRoute>} />
                  {/* WAYP-389: Purchase Orders list. ATL-000801 — the gate was
                      temporarily unwrapped (TODO WAYP-385) so the screen could be
                      manually tested before the user-permissions UI existed. That
                      UI has since shipped (UserPermissionsModal in SettingsPage),
                      so admins can grant `purchase_orders.view` and the route is
                      guarded again like every other module route. */}
                  <Route path="/purchase-orders" element={<PermissionRoute module="purchase_orders"><PurchaseOrdersPage /></PermissionRoute>} />
                  <Route path="/financial/report-catalog" element={<PermissionRoute module="reports"><ReportCatalogPage /></PermissionRoute>} />
                  {/* #1077: Received Payments lives under the `invoices` permission family. */}
                  <Route path="/financial/received-payments" element={<PermissionRoute module="invoices"><ReceivedPaymentsPage /></PermissionRoute>} />
                  {/* WAYP-392: Expenses module (V1 parity port) */}
                  <Route path="/financial/expenses" element={<PermissionRoute module="expenses"><ExpensesPage /></PermissionRoute>} />
                  {/* #1087: Custom Charges / Credits — Finance menu V1→V2 foundation */}
                  <Route path="/financial/custom-charges" element={<PermissionRoute module="custom_charges"><CustomChargesPage /></PermissionRoute>} />
                  {/* Other Charges or Reimbursements — cross-load list, V1 parity */}
                  <Route path="/financial/other-charges-reimbursements" element={<PermissionRoute module="loads"><OtherChargesReimbursementsPage /></PermissionRoute>} />
                  {/* #1137: Customer Statements & Collections — feature-gated server-side
                      via `enable_statements_collections`; sidebar hides the entry when
                      the feature is OFF. Permission piggybacks on `invoices`. */}
                  <Route path="/financial/customer-statements" element={<PermissionRoute module="invoices"><CustomerStatementsPage /></PermissionRoute>} />

                  {/* Reports */}
                  <Route path="/reports" element={<PermissionRoute module="reports"><ReportsPage /></PermissionRoute>} />
                  {/* #1573 — stub coming-soon landing for un-ported V1 reports. */}
                  <Route path="/reports/coming-soon" element={<PermissionRoute module="reports"><ReportComingSoonPage /></PermissionRoute>} />
                  {/* #1641 — Sales by Customer (Detail + Summary). `?view=summary`
                      flips the active tab on mount. */}
                  <Route path="/reports/sales-by-customer" element={<PermissionRoute module="reports"><SalesByCustomerReportPage /></PermissionRoute>} />
                  {/* #1698 — Settlements By Driver/Carrier - Detail ("money out").
                      `?view=summary` flips to per-payee rollup on mount. */}
                  <Route path="/reports/settlements-driver-carrier-detail" element={<PermissionRoute module="reports"><SettlementsDriverCarrierDetailReportPage /></PermissionRoute>} />
                  {/* #1722 — Unsettled Loads (working-capital / AR-aging report). */}
                  <Route path="/reports/unsettled-loads" element={<PermissionRoute module="reports"><UnsettledLoadsReportPage /></PermissionRoute>} />
                  {/* #1714 — Equipment Utilization (Detail) — per-equipment
                      utilization + earning-status breakdown under Dispatch. */}
                  <Route path="/reports/equipment-utilization-detail" element={<PermissionRoute module="reports"><EquipmentUtilizationReportPage /></PermissionRoute>} />
                  {/* #1657 — Scheduled Trip Miles - Detail (Contractors/USPS Reports). */}
                  <Route path="/reports/scheduled-trip-miles-detail" element={<PermissionRoute module="reports"><ScheduledTripMilesDetailReportPage /></PermissionRoute>} />
                  {/* V1 SummaryForContractTripDriverLocation + DetailForContractTripDriverLocation.
                      USPS on-time compliance. `?view=summary` flips to grouped rollup on mount. */}
                  <Route path="/reports/on-time-appointment" element={<PermissionRoute module="reports"><OnTimeAppointmentReportPage /></PermissionRoute>} />
                  {/* #1701 — Completed Loads Detail report + Billing Backup
                      tab. `?view=billing-backups` flips to the backups tab on
                      mount; backup snapshot viewer renders inline. */}
                  <Route path="/reports/completed-loads-detail" element={<PermissionRoute module="reports"><CompletedLoadsDetailReportPage /></PermissionRoute>} />
                  {/* #1691 — Payroll Company Driver Hours and Pay Detail.
                      `?view=summary` flips the active tab on mount. */}
                  <Route path="/reports/driver-hours-pay-detail" element={<PermissionRoute module="reports"><DriverHoursPayDetailReportPage /></PermissionRoute>} />
                  {/* V1 ProfitLoad + ProfitCustomer — P&L Per Load Detail + Per Customer Summary.
                      `?view=summary` flips to per-customer rollup on mount. */}
                  <Route path="/reports/profit-loss-per-load" element={<PermissionRoute module="reports"><ProfitLossPerLoadReportPage /></PermissionRoute>} />
                  <Route path="/reports/deductions-per-driver-carrier" element={<PermissionRoute module="reports"><DeductionsPerDriverCarrierReportPage /></PermissionRoute>} />
                  <Route path="/reports/invoiced-work-orders" element={<PermissionRoute module="reports"><InvoicedWorkOrdersReportPage /></PermissionRoute>} />
                  <Route path="/reports/profit-loss-summary" element={<PermissionRoute module="reports"><ProfitLossSummaryReportPage /></PermissionRoute>} />
                  <Route path="/reports/equipment-cost" element={<PermissionRoute module="reports"><EquipmentCostReportPage /></PermissionRoute>} />
                  <Route path="/reports/driver-sleeper-berth" element={<PermissionRoute module="reports"><DriverSleeperBerthReportPage /></PermissionRoute>} />
                  <Route path="/reports/driver-shifts" element={<PermissionRoute module="reports"><DriverShiftsReportPage /></PermissionRoute>} />
                  <Route path="/reports/other-cost-per-driver-carrier" element={<PermissionRoute module="reports"><OtherCostPerDriverCarrierReportPage /></PermissionRoute>} />
                  <Route path="/reports/other-cost-per-equipment" element={<PermissionRoute module="reports"><OtherCostPerEquipmentReportPage /></PermissionRoute>} />
                  <Route path="/reports/settlements-driver-carrier-summary" element={<PermissionRoute module="reports"><SettlementsDriverCarrierSummaryReportPage /></PermissionRoute>} />
                  <Route path="/reports/settlement-payments" element={<PermissionRoute module="reports"><SettlementPaymentsReportPage /></PermissionRoute>} />
                  <Route path="/reports/reimbursements-per-driver-carrier" element={<PermissionRoute module="reports"><ReimbursementsPerDriverCarrierReportPage /></PermissionRoute>} />
                  <Route path="/reports/estimated-miles" element={<PermissionRoute module="reports"><EstimatedMilesReportPage /></PermissionRoute>} />
                  <Route path="/reports/extra-trips" element={<PermissionRoute module="reports"><ExtraTripsReportPage /></PermissionRoute>} />
                  <Route path="/reports/miles-detail" element={<PermissionRoute module="reports"><MilesDetailReportPage /></PermissionRoute>} />
                  <Route path="/reports/missing-shift" element={<PermissionRoute module="reports"><MissingShiftReportPage /></PermissionRoute>} />
                  <Route path="/reports/slips-per-driver-summary" element={<PermissionRoute module="reports"><SlipsPerDriverSummaryReportPage /></PermissionRoute>} />
                  <Route path="/reports/tenders-not-accepted-new-trips" element={<PermissionRoute module="reports"><TendersNotAcceptedNewTripsReportPage /></PermissionRoute>} />
                  <Route path="/reports/tenders-not-accepted-time-changes" element={<PermissionRoute module="reports"><TendersNotAcceptedTimeChangesReportPage /></PermissionRoute>} />
                  <Route path="/reports/tenders-not-accepted-trips-not-removed" element={<PermissionRoute module="reports"><TendersNotAcceptedTripsNotRemovedReportPage /></PermissionRoute>} />
                  <Route path="/reports/tenders-not-accepted-stops-not-removed" element={<PermissionRoute module="reports"><TendersNotAcceptedStopsNotRemovedReportPage /></PermissionRoute>} />
                  <Route path="/reports/tenders-rejected" element={<PermissionRoute module="reports"><TendersRejectedReportPage /></PermissionRoute>} />
                  <Route path="/reports/time-off" element={<PermissionRoute module="reports"><TimeOffReportPage /></PermissionRoute>} />
                  <Route path="/reports/driver-expiries" element={<PermissionRoute module="reports"><DriverExpiriesReportPage /></PermissionRoute>} />
                  <Route path="/reports/equipment-expiries" element={<PermissionRoute module="reports"><EquipmentExpiriesReportPage /></PermissionRoute>} />
                  {/* Subscription Details — cross-org, super_admin only (ATL-000349). */}
                  <Route path="/reports/subscription-details" element={<RequireSuperAdmin><SubscriptionDetailsReportPage /></RequireSuperAdmin>} />
                  <Route path="/reports/slips-detail" element={<PermissionRoute module="reports"><SlipsDetailReportPage /></PermissionRoute>} />
                  <Route path="/reports/tenders-not-accepted-new-stops" element={<PermissionRoute module="reports"><TendersNotAcceptedNewStopsReportPage /></PermissionRoute>} />
                  <Route path="/reports/geofence-log" element={<PermissionRoute module="reports"><GeofenceLogReportPage /></PermissionRoute>} />
                  <Route path="/reports/profit-loss-per-customer-trip" element={<PermissionRoute module="reports"><ProfitLossPerCustomerTripReportPage /></PermissionRoute>} />
                  <Route path="/reports/profit-loss-per-driver-carrier" element={<PermissionRoute module="reports"><ProfitLossPerDriverCarrierReportPage /></PermissionRoute>} />
                  <Route path="/reports/contract-rate-per-mile" element={<PermissionRoute module="reports"><ContractRatePerMileReportPage /></PermissionRoute>} />
                  <Route path="/reports/profit-loss-per-trip" element={<PermissionRoute module="reports"><ProfitLossPerTripReportPage /></PermissionRoute>} />
                  <Route path="/reports/profit-loss-per-truck-equipment" element={<PermissionRoute module="reports"><ProfitLossPerTruckEquipmentReportPage /></PermissionRoute>} />
                  {/* Quotes — V1-parity quotes report. `?view=summary` flips to
                      the per-status rollup. */}
                  <Route path="/reports/quotes" element={<PermissionRoute module="reports"><QuotesReportPage /></PermissionRoute>} />
                  {/* Sales by Dispatcher - Detail (V1-parity). `?view=summary`
                      flips to the per-dispatcher rollup. */}
                  <Route path="/reports/sales-by-dispatcher" element={<PermissionRoute module="reports"><SalesByDispatcherDetailReportPage /></PermissionRoute>} />
                  {/* Driver Activity — DB-backed per-driver shift/stop activity log
                      (V1-parity). `?view=summary` flips to the per-driver rollup. */}
                  <Route path="/reports/driver-activity" element={<PermissionRoute module="reports"><DriverActivityReportPage /></PermissionRoute>} />
                  {/* Notes Report — DB-backed per-note listing (V1-parity).
                      `?view=summary` flips to the per-department rollup. */}
                  <Route path="/reports/notes" element={<PermissionRoute module="reports"><NotesReportPage /></PermissionRoute>} />
                  {/* Completed Loads Detail, Billing Information (V1-parity).
                      `?view=summary` flips to the per-customer rollup. */}
                  <Route path="/reports/completed-loads-billing" element={<PermissionRoute module="reports"><CompletedLoadsBillingReportPage /></PermissionRoute>} />
                  <Route path="/reports/open-loads-billing" element={<PermissionRoute module="reports"><OpenLoadsBillingReportPage /></PermissionRoute>} />
                  <Route path="/reports/open-loads-detail" element={<PermissionRoute module="reports"><OpenLoadsDetailReportPage /></PermissionRoute>} />
                  <Route path="/reports/open-loads-routing" element={<PermissionRoute module="reports"><OpenRoutingReportPage /></PermissionRoute>} />
                  {/* Sales by Equipment - Detail (V1-parity). `?view=summary`
                      flips to the per-equipment rollup. */}
                  <Route path="/reports/sales-by-equipment" element={<PermissionRoute module="reports"><SalesByEquipmentReportPage /></PermissionRoute>} />
                  <Route path="/reports/completed-loads-routing" element={<PermissionRoute module="reports"><CompletedRoutingReportPage /></PermissionRoute>} />
                  {/* Sales by Rate Type — Summary + Detail (V1-parity).
                      `?view=summary` flips to the per-rate-type rollup. */}
                  <Route path="/reports/sales-by-rate-type" element={<PermissionRoute module="reports"><SalesByRateTypeReportPage /></PermissionRoute>} />
                  {/* Open Invoices — outstanding-balance invoice list (V1-parity).
                      `?view=summary` flips to the per-customer rollup. */}
                  <Route path="/reports/open-invoices" element={<PermissionRoute module="reports"><OpenInvoicesReportPage /></PermissionRoute>} />
                  {/* Uninvoiced Loads — completed loads with no active invoice
                      (V1-parity). `?view=summary` flips to the per-customer rollup. */}
                  <Route path="/reports/uninvoiced-loads" element={<PermissionRoute module="reports"><UninvoicedLoadsReportPage /></PermissionRoute>} />
                  {/* Accounts Receivable Aging - Detail (V1-parity). `?view=summary`
                      flips to the per-customer aging matrix. */}
                  <Route path="/reports/ar-aging" element={<PermissionRoute module="reports"><AccountsReceivableAgingDetailReportPage /></PermissionRoute>} />
                  <Route path="/reports/deadhead-miles" element={<PermissionRoute module="reports"><DeadheadMilesReportPage /></PermissionRoute>} />
                  <Route path="/reports/fuel-tax-summary" element={<PermissionRoute module="reports"><FuelTaxSummaryReportPage /></PermissionRoute>} />
                  <Route path="/reports/fuel-tax-history" element={<PermissionRoute module="reports"><FuelTaxHistoryReportPage /></PermissionRoute>} />
                  {/* Mileage — Detail / By Truck / By State tabs (`?view=truck|state`). */}
                  <Route path="/reports/mileage" element={<PermissionRoute module="reports"><MileageReportPage /></PermissionRoute>} />
                  {/* Legacy split reports; kept as redirects so bookmarks and
                      saved favorites still resolve. */}
                  <Route path="/reports/mileages-detail" element={<Navigate to="/reports/mileage" replace />} />
                  <Route path="/reports/mileages-summary" element={<Navigate to="/reports/mileage?view=truck" replace />} />
                  <Route path="/reports/sales-commission" element={<PermissionRoute module="reports"><SalesCommissionReportPage /></PermissionRoute>} />
                  {/* Profit Commission (V1 ProfitCommission) is the same surface
                      as Sales Commission — V1 ships them as two reports over an
                      identical view, shown according to the org's
                      `commission_based_on_sale_or_profit` basis. Its own path so
                      the URL matches the report name; the page titles itself from
                      the basis returned with the data. */}
                  <Route path="/reports/profit-commission" element={<PermissionRoute module="reports"><SalesCommissionReportPage /></PermissionRoute>} />
                  <Route path="/reports/commission" element={<PermissionRoute module="reports"><CommissionReportPage /></PermissionRoute>} />
                  <Route path="/audit-logs" element={<PermissionRoute module="reports"><AuditLogPage /></PermissionRoute>} />

                  {/* Cross-dock */}
                  <Route path="/cross-dock" element={<PermissionRoute module="cross_dock"><CrossDockPage /></PermissionRoute>} />

                  {/* Specialty freight */}
                  <Route path="/livestock" element={<RequireFeature featureKey="mod_livestock_main"><PermissionRoute module="loads"><LivestockPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/edi" element={<PermissionRoute module="edi"><EDIPage /></PermissionRoute>} />
                  {/* FourKites tracking dashboard. The nav entry now lives under
                      USPS Carrier > Loads (moved out of Operations), but the URL
                      stays here so bookmarks keep working. Surfaced in nav only
                      when the org has a FourKites-enabled EDI trading partner
                      AND `mod_usps_main` is on. Guarded by edi:view (matches the
                      server route). */}
                  <Route path="/operations/fourkites-monitoring" element={<PermissionRoute module="edi"><FourkitesMonitoringPage /></PermissionRoute>} />
                  {/* #1216 USPS Legacy Cleanup (Andrew 2026-05-21):
                      `/usps` route retired alongside UspsPage. */}
                  {/* WAYP-505 — top-level USPS Carrier routes, gated by usps:view + mod_usps_main.
                      ATL-000381 follow-up: every USPS Carrier route now carries a
                      route-level RequireFeature('mod_usps_main') gate (redirects to
                      /dashboard when the module is OFF), replacing the old
                      "menu-only gate, route reachable by direct URL" convention —
                      the server already 403s these APIs, so direct URLs only ever
                      rendered a broken shell. */}
                  <Route path="/usps/mail-class-types" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps"><MailClassTypesPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/usps/mail-purpose-types" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps"><MailPurposeTypesPage /></PermissionRoute></RequireFeature>} />
                  {/* USPS-CONTRACTS-06 (2026-04-29) / URL reverted 2026-04-30:
                      UspsContract canonical route is `/usps/contracts`
                      (the original WAYP-177 path) under the USPS Carrier
                      nav group. Permission scope: `contracts:view`
                      (was `usps:view`) — the dedicated `contracts` scope
                      is registered in USPS-CONTRACTS-02. ATL-000381
                      follow-up: now ALSO gated on mod_usps_main at the
                      route level — the server serves this module under the
                      feature-gated /api/v1/usps router, so without the
                      gate a direct URL only rendered a 403-storm shell. The
                      briefly-shipped `/usps-carrier/contracts` path
                      redirects here so any bookmarks from that window
                      still work. */}
                  <Route
                    path="/usps/contracts"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="contracts"><UspsContractsPage /></PermissionRoute></RequireFeature>}
                  />
                  <Route path="/usps-carrier/contracts" element={<Navigate to="/usps/contracts" replace />} />
                  {/* WAYP-764 (umbrella) — USPS Trips V1-parity rebuild.
                      Replaces the legacy `Operations → USPS → Trips` tab
                      (removed in the same slice). Permission scope:
                      `trips:view` (registered in slice 02 of the umbrella).
                      Mounted under USPS Carrier nav group at /usps/trips,
                      consistent with the post-2026-04-30 path scheme used
                      by /usps/contracts. ATL-000381 follow-up: route-level
                      mod_usps_main gate added (see block comment above). */}
                  <Route
                    path="/usps/trips"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="trips"><UspsTripsPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* #1141 (USPS Carrier Loads V2 umbrella) — new USPS Loads
                      page at /usps/loads. Slice #1144 ships the Weekly
                      Rolling Window header; sub-sections (Unassigned Trips
                      #1145 + Loads grid #1146) plug into the same page
                      container. Permission scope `usps_loads:view`
                      (registered in slice #1142). ATL-000381 follow-up:
                      route-level mod_usps_main gate added (see block
                      comment above). */}
                  {/* #2302 (Andrew 2026-08-06) — USPS Loads is retired from
                      navigation in favour of the USPS Planner. The grid route
                      redirects so existing bookmarks land on the Planner
                      instead of a page with no way back into the nav. Only the
                      GRID redirects: /usps/loads/new and /usps/loads/:id/edit
                      below are still live (the Planner opens the same form).
                      `replace` keeps the dead URL out of history, so Back from
                      the Planner returns to wherever the user actually came
                      from rather than bouncing through the redirect. */}
                  <Route path="/usps/loads" element={<Navigate to="/usps/planner" replace />} />
                  {/* #2302 — USPS Planner: USPS-only multi-view planner. Same
                      feature + permission gates as USPS Loads. */}
                  <Route
                    path="/usps/planner"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps_loads"><UspsPlannerPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* USPS 5500 (#1556) — slip-reason inquiry grid. Gated by
                      `usps_5500:view`; the Sidebar entry adds the
                      `mod_usps_main` feature + `organization.usps500`
                      preference gates. Route stays reachable by direct URL
                      (matching the other USPS pages). */}
                  <Route
                    path="/usps/5500"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps_5500"><Usps5500Page /></PermissionRoute></RequireFeature>}
                  />
                  {/* USPS Renewal (#1922) — renewal cycle grid. `usps_renewals:view`
                      perm; the Sidebar entry adds the `mod_usps_main` feature gate. */}
                  <Route
                    path="/usps/renewals"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps_renewals"><RenewalsPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* Reconcile Slips (#1564) — SSRM grid (complement of the
                      5500 view). `reconcile_slips:view` perm; nav enforces the
                      `mod_usps_main` feature + `usps500` preference gates. */}
                  <Route
                    path="/usps/reconcile-slips"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="reconcile_slips"><ReconcileSlipsPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* CLEAR Rates (#1652) — imported USPS CLEAR rate sheets.
                      `clear_rates:view` perm; nav enforces the `mod_usps_main`
                      feature + `usps500` preference gates. */}
                  <Route
                    path="/usps/clear-rates"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="clear_rates"><ClearRatesPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* FourKites weekly compliance scores. `fourkites_compliance:view`
                      perm; nav enforces the `mod_usps_main` feature. super_admins
                      additionally get the upload/cross-org surface on the page. */}
                  <Route
                    path="/usps/compliance-scores"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="fourkites_compliance"><ComplianceScoresPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* #1763 — USPS Voucher Import. `usps_vouchers:view` perm; nav
                      enforces the `mod_usps_main` feature + `usps500` preference. */}
                  <Route
                    path="/usps/vouchers"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps_vouchers"><VoucherImportPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* #1770 — USPS Reconciliation 3-tab page. `usps_reconciliation:view`
                      perm; nav enforces `mod_usps_main` + `usps500` preference. */}
                  <Route
                    path="/usps/reconciliation"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps_reconciliation"><ReconciliationPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* #1770 — No Miles Reconciliation (own page). */}
                  <Route
                    path="/usps/no-miles-reconciliation"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps_reconciliation"><NoMilesReconciliationPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* #1288 — USPS Load History (read-only Completed-loads
                      grid). Same `usps_loads:view` perm as the active
                      page; nav entry in Sidebar enforces the
                      `mod_usps_main` feature gate. Route declared before
                      `:id/edit` for clarity even though react-router v6+
                      prefers static segments over dynamic regardless of
                      declaration order. */}
                  {/* #2302 — retired alongside the grid; see the note above. */}
                  <Route path="/usps/loads/history" element={<Navigate to="/usps/planner" replace />} />
                  {/* #1147 — USPS Load form (Phase 2 of the umbrella).
                      Both routes are NEW codepath (NOT dispatch-loads); they
                      gate on `usps_loads:create` and `usps_loads:edit` (perms
                      registered in slice #1142). The form is lazy-loaded so
                      it doesn't bloat the initial bundle. */}
                  <Route
                    path="/usps/loads/new"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps_loads" action="create"><UspsLoadFormCreatePage /></PermissionRoute></RequireFeature>}
                  />
                  <Route
                    path="/usps/loads/:id/edit"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps_loads" action="edit"><UspsLoadFormEditPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* Umbrella #966 (slice f/i) — USPS Recurring Loads page.
                      Permission scope `recurring:view` (slice b). Two
                      additional gates run inside the page component:
                        - FeatureGate `mod_usps_main` (layer 1)
                        - Pref `enableRecurringLoads` (layer 3) — surfaces
                          a "not enabled" card when OFF, so direct deep
                          links don't bypass the per-org opt-in.
                      The layer-2 permission gate is the PermissionRoute
                      wrapper below. Slice g will land the form route(s)
                      under `/usps/recurring-loads/new` + `/:id`. */}
                  <Route
                    path="/usps/recurring-loads"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="recurring"><RecurringLoadsPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* WAYP-973 (slice g/i) — Recurring Load create/edit form.
                      Two routes mount the same component in different modes;
                      both gated `recurring:create` / `recurring:edit` (slice
                      b). Existing page-level FeatureGate / pref-gate live in
                      RecurringLoadsPage; the form trusts the route as a
                      precondition — direct deep-links still go through the
                      module-level permissions guard. */}
                  <Route
                    path="/usps/recurring-loads/new"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="recurring" action="create"><RecurringLoadFormCreatePage /></PermissionRoute></RequireFeature>}
                  />
                  <Route
                    path="/usps/recurring-loads/:id/edit"
                    element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="recurring" action="edit"><RecurringLoadFormEditPage /></PermissionRoute></RequireFeature>}
                  />
                  {/* WAYP-927 — Driver Routes workspace (V1 /bids/ → V2 port).
                      The legacy /usps/bids workspace (WAYP-228) was removed in the
                      2026-05-15 batch. Andrew 2026-05-30: URL rebased from
                      /driver-routes to /usps/driver-routes to sit under the
                      USPS Carrier nav alongside Trips + Recurring Loads. No
                      bookmark redirect from the old path — internal-only
                      replacement. */}
                  <Route path="/usps/driver-routes" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="driver_routes"><DriverRoutesPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/usps/driver-routes/new" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="driver_routes"><DriverRouteDetailPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/usps/driver-routes/:id" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="driver_routes"><DriverRouteDetailPage /></PermissionRoute></RequireFeature>} />
                  {/* USPS Tenders V2 — quotes-based tender queue (replaces the
                      WAYP-231 UspsEdiTender queue at this same route). */}
                  <Route path="/usps/tenders" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="usps_tenders"><UspsTendersPage /></PermissionRoute></RequireFeature>} />
                  {/* Process EDI (#1755) — USPS EDI exception-review console.
                      Route gate matches the Sidebar entry: `mod_usps_main` as
                      layer 1 plus the `edi` feature on top.
                      ATL-000726 — this was the only /usps/* route NOT gated on
                      `mod_usps_main`, so a General Freight org with the `edi`
                      feature on could still deep-link into a USPS page. Both
                      features are required: it is a USPS surface (mod_usps_main)
                      that happens to consume EDI (edi). Nested rather than
                      combined because RequireFeature takes a single key. */}
                  <Route path="/usps/process-edi" element={<RequireFeature featureKey="mod_usps_main"><RequireFeature featureKey="edi"><PermissionRoute module="usps_process_edi"><ProcessEdiPage /></PermissionRoute></RequireFeature></RequireFeature>} />
                  {/* #1635 — FarEye inbound API Tenders. Gated on the `api_tendering`
                      enterprise feature (RequireFeature, redirects to /dashboard when
                      OFF — the org configures the FarEye integration as part of that
                      feature's setup) AND the `edi` permission scope (matches the
                      server router: api_tendering feature + edi:view / edi:process). */}
                  <Route path="/dispatch/api-tenders" element={<RequireFeature featureKey="api_tendering"><PermissionRoute module="edi"><FareyeTenderQueuePage /></PermissionRoute></RequireFeature>} />
                  {/* History must be registered BEFORE the `:id` route so
                      "history" isn't captured as a tender id. */}
                  <Route path="/dispatch/api-tenders/history" element={<RequireFeature featureKey="api_tendering"><PermissionRoute module="edi"><FareyeTenderHistoryPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/dispatch/api-tenders/:id" element={<RequireFeature featureKey="api_tendering"><PermissionRoute module="edi"><FareyeTenderDetailPage /></PermissionRoute></RequireFeature>} />
                  {/* ATL-001415 — AI Email Intake. Gated on the `ai_email_load_creation`
                      enterprise feature + the `email_intake` permission (matches the server router). */}
                  <Route path="/dispatch/email-intake" element={<RequireFeature featureKey="ai_email_load_creation"><PermissionRoute module="email_intake"><EmailIntakeQueuePage /></PermissionRoute></RequireFeature>} />
                  <Route path="/dispatch/email-intake/:id" element={<RequireFeature featureKey="ai_email_load_creation"><PermissionRoute module="email_intake"><EmailIntakeDetailPage /></PermissionRoute></RequireFeature>} />
                  {/* ATL-001415 — Email Intake settings live under Settings → Integrations; the old address redirects. */}
                  <Route path="/admin/integrations/email-intake" element={<RequireFeature featureKey="ai_email_load_creation"><PermissionRoute module="email_intake"><EmailIntakeSettingsPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/settings/email-intake" element={<LegacyEmailIntakeSettingsRedirect />} />
                  <Route path="/reefer" element={<PermissionRoute module="loads"><ReeferPage /></PermissionRoute>} />
                  <Route path="/freight" element={<RequireFeature featureKey="mod_ftl_brokerage_main"><PermissionRoute module="loads"><FreightPage /></PermissionRoute></RequireFeature>} />

                  {/* Admin */}
                  <Route path="/settings" element={<PermissionRoute module="settings"><SettingsPage /></PermissionRoute>} />
                  {/* Super-admin only. Sidebar entry is also `superAdminOnly`
                      so non-super-admins never see the link. The guard here
                      is the actual enforcement — redirects to /dashboard
                      when a non-super-admin types the URL directly. */}
                  <Route path="/settings/api-keys" element={<RequireSuperAdmin><IntegrationApiKeysPage /></RequireSuperAdmin>} />
                  <Route path="/settings/dashboard-permissions" element={<PermissionRoute module="settings"><DashboardPermissionsPage /></PermissionRoute>} />
                  <Route path="/settings/company-info" element={<PermissionRoute module="settings"><CompanyInfoPage /></PermissionRoute>} />
                  {/* WAYP-803: Company Preferences (V1 parity — round 1 = General tab) */}
                  <Route path="/settings/company-preferences" element={<PermissionRoute module="settings"><CompanyPreferencesPage /></PermissionRoute>} />
                  <Route path="/settings/deduction-types"         element={<PermissionRoute module="deduction_types"><DeductionTypesPage /></PermissionRoute>} />
                  <Route path="/settings/equipment-types"         element={<PermissionRoute module="equipment_types"><EquipmentTypesPage /></PermissionRoute>} />
                  <Route path="/settings/driver-rating-questions" element={<PermissionRoute module="driver_rating_questions"><DriverRatingQuestionsPage /></PermissionRoute>} />
                  <Route path="/settings/driver-expiry-types"    element={<PermissionRoute module="driver_expiry_types"><DriverExpiryTypesPage /></PermissionRoute>} />
                  <Route path="/settings/time-off-types"          element={<PermissionRoute module="time_off_types"><TimeOffTypesPage /></PermissionRoute>} />
                  <Route path="/settings/expense-types"  element={<PermissionRoute module="expense_types"><ExpenseTypesPage /></PermissionRoute>} />
                  <Route path="/settings/department-types" element={<PermissionRoute module="department_types"><DepartmentTypesPage /></PermissionRoute>} />
                  <Route path="/settings/custom-templates" element={<PermissionRoute module="custom_templates"><CustomTemplatesPage /></PermissionRoute>} />
                  <Route path="/settings/carrier-requirement-types" element={<PermissionRoute module="carrier_requirement_types"><CarrierRequirementTypesPage /></PermissionRoute>} />
                  {/* WAYP-837 — Location Types (V1 parity). LocationType is a child concept of the Locations module so it gates on `locations:view`. */}
                  <Route path="/settings/location-types" element={<PermissionRoute module="locations"><LocationTypesPage /></PermissionRoute>} />
                  <Route path="/settings/taxes" element={<PermissionRoute module="taxes"><TaxesPage /></PermissionRoute>} />
                  <Route path="/settings/trading-partners" element={<PermissionRoute module="trading_partners"><TradingPartnersPage /></PermissionRoute>} />
                  <Route path="/settings/fuel-rates" element={<PermissionRoute module="fuel_rates"><FuelRateSettingsPage /></PermissionRoute>} />
                  <Route path="/settings/equipment-expiry-types" element={<PermissionRoute module="equipment_expiry_types"><EquipmentExpiriesPage /></PermissionRoute>} />
                  <Route path="/settings/terminals" element={<PermissionRoute module="terminals"><TerminalSettingsPage /></PermissionRoute>} />
                  {/* #1216 USPS Legacy Cleanup (Andrew 2026-05-21):
                      `/settings/usps-carrier` route retired alongside
                      its page + 8 ModuleSetting fields. */}
                  <Route path="/settings/file-checklist" element={<PermissionRoute module="file_checklist"><FileChecklistPage /></PermissionRoute>} />
                  <Route path="/settings/routing-row-types" element={<PermissionRoute module="routing_row_types"><RoutingRowTypesPage /></PermissionRoute>} />
                  <Route path="/settings/work-order-types" element={<PermissionRoute module="work_order_types"><WorkOrderTypesPage /></PermissionRoute>} />
                  <Route path="/settings/purchase-order-types" element={<PermissionRoute module="purchase_order_types"><PurchaseOrderTypesPage /></PermissionRoute>} />
                  <Route path="/settings/load-categories" element={<PermissionRoute module="load_categories"><LoadCategoriesPage /></PermissionRoute>} />
                  <Route path="/settings/commodity-categories" element={<PermissionRoute module="commodity_categories"><CommodityCategoriesPage /></PermissionRoute>} />
                  {/* WAYP-45 (Round 6): Custom Dispatch Tabs standalone page. */}
                  <Route path="/settings/custom-dispatch-tabs" element={<PermissionRoute module="custom_dispatch_tabs"><CustomDispatchTabsPage /></PermissionRoute>} />
                  {/* WAYP-681 (umbrella #677) — Frequency Types. Page-level visibility gate hides UI when USPS is off / hide_contracts_and_trips=true; server returns 404. URL moved to /usps/frequency-types to match the USPS Carrier nav group; the legacy /settings/frequency-types path redirects for back-compat with existing bookmarks. */}
                  <Route path="/usps/frequency-types" element={<RequireFeature featureKey="mod_usps_main"><PermissionRoute module="frequency_types"><FrequencyTypesPage /></PermissionRoute></RequireFeature>} />
                  <Route path="/settings/frequency-types" element={<Navigate to="/usps/frequency-types" replace />} />
                  <Route path="/settings/load-statuses" element={<PermissionRoute module="load_statuses"><LoadStatusesPage /></PermissionRoute>} />
                  <Route path="/settings/commission-rules" element={<PermissionRoute module="settings"><CommissionRulesPage /></PermissionRoute>} />
                  <Route path="/settings/slips" element={<PermissionRoute module="slips"><SlipsPage /></PermissionRoute>} />
                  <Route path="/types/slips" element={<Navigate to="/settings/slips" replace />} />
                  <Route path="/settings/custom-data-types" element={<PermissionRoute module="custom_data_types"><CustomDataTypesPage /></PermissionRoute>} />
                  <Route path="/settings/rate-types" element={<PermissionRoute module="rate_service_types"><RateServiceTypesPage /></PermissionRoute>} />
                  {/* Weight-Based Zone Rating — dedicated rate card screen for a weight_zone rate type. */}
                  <Route path="/settings/rate-types/:id/rate-card" element={<PermissionRoute module="rate_service_types"><ZoneRateCardPage /></PermissionRoute>} />
                  {/* Weight-Based Zone Rating — Zones master. Permission module `zones`. */}
                  <Route path="/settings/zones" element={<PermissionRoute module="zones"><ZonesPage /></PermissionRoute>} />
                  <Route path="/settings/other-charge-types" element={<PermissionRoute module="other_charge_types"><OtherChargeTypesPage /></PermissionRoute>} />
                  <Route path="/admin/jobs" element={<PermissionRoute module="settings"><JobQueuePage /></PermissionRoute>} />
                  <Route path="/admin/gps-devices" element={<PermissionRoute module="maps"><GpsDeviceAdminPage /></PermissionRoute>} />
                  <Route path="/admin/features" element={<PermissionRoute module="settings"><FeatureManagementPage /></PermissionRoute>} />
                  <Route path="/admin/integrations" element={<PermissionRoute module="integrations"><IntegrationAdminPage /></PermissionRoute>} />
                  <Route path="/admin/onboard-customer" element={<PermissionRoute module="settings"><OnboardCustomerPage /></PermissionRoute>} />
                  {/* #1225 slice 7 — Scheduled Commands page. Super-admin
                      only; the gate is enforced both by the wrapper here
                      AND by the server-side `requireSuperAdmin` middleware
                      on /api/v1/admin/commands so a leaked URL still 401s. */}
                  <Route
                    path="/admin/commands"
                    element={
                      <RequireSuperAdmin>
                        <CommandsPage />
                      </RequireSuperAdmin>
                    }
                  />

                  {/* Switch User (admin impersonation). Super-admin only;
                      gated here AND by `requireSuperAdmin` on
                      /api/v1/users/switch* so a leaked URL still 403s. */}
                  <Route
                    path="/admin/switch-user"
                    element={
                      <RequireSuperAdmin>
                        <SwitchUserPage />
                      </RequireSuperAdmin>
                    }
                  />

                  {/* WAYP-311: Multi-currency & cross-border */}
                  {/* #1077: Exchange Rates gates on the `invoices:view` perm
                      (currency context is part of the invoicing workflow). */}
                  <Route path="/international/exchange-rates" element={<PermissionRoute module="invoices"><ExchangeRatesPage /></PermissionRoute>} />
                  <Route path="/international/tax-rates" element={<PermissionRoute module="settings"><TaxRatesPage /></PermissionRoute>} />
                  {/* WAYP-395 Phase E.1: V2 Customs Brokers page — full SSRM grid,
                      bulk import/export, restore + audit logging. Permission module
                      `customs_brokers`; render-time gate also checks the
                      `mod_customs_broker` enterprise feature, the
                      `enable_custom_broker_info` operational toggle, and
                      `companyType ∈ {broker, mixed}` (V1 parity). */}
                  <Route path="/freight/customs-brokers" element={<PermissionRoute module="customs_brokers"><CustomsBrokersPage /></PermissionRoute>} />
                  {/* WAYP-319: QuickBooks Desktop Integration */}
                  <Route path="/settings/quickbooks-desktop" element={<PermissionRoute module="settings"><QbdIntegrationPage /></PermissionRoute>} />
                  {/* #1649: QuickBooks Online Integration */}
                  <Route path="/settings/quickbooks-online" element={<PermissionRoute module="settings"><QuickBooksOnlinePage /></PermissionRoute>} />
                  {/* ATL-000973: the Xero mapping page moved into the entity forms + General Settings → Xero. */}
                  <Route path="/settings/xero" element={<Navigate to="/settings?tab=xero" replace />} />
                  {/* #1731 — Google Calendar integration (also the OAuth return URL). */}
                  <Route path="/settings/google-calendar" element={<PermissionRoute module="settings"><GoogleCalendarPage /></PermissionRoute>} />
                  {/* #1740 — Outlook Calendar integration (also the OAuth return URL). */}
                  <Route path="/settings/outlook-calendar" element={<PermissionRoute module="settings"><OutlookCalendarPage /></PermissionRoute>} />

                  {/* WAYP-326: FreightBid AI — Phase 4 Frontend */}
                  <Route path="/freightbid" element={<FreightBidScenariosPage />} />
                  <Route path="/freightbid/scenarios/:id" element={<FreightBidScenarioDetailPage />} />
                  <Route path="/freightbid/compare" element={<FreightBidComparePage />} />

                  {/* Profile & badges — accessible to all authenticated users */}
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/badges" element={<BadgesPage />} />
                  {/* #1535 — Notification Center. Gated behind the
                      `notifications_center` enterprise feature (default ON);
                      RequireFeature redirects to /dashboard when the toggle
                      is off so the bell remains the only surface. */}
                  <Route
                    path="/notifications"
                    element={
                      <RequireFeature featureKey="notifications_center">
                        <PermissionRoute module="notifications">
                          <NotificationCenterPage />
                        </PermissionRoute>
                      </RequireFeature>
                    }
                  />
                  <Route path="/documents/view" element={<PermissionRoute module="documents"><DocumentViewer /></PermissionRoute>} />

                  {/* Support */}
                  <Route path="/support" element={<PermissionRoute module="support"><SupportPage /></PermissionRoute>} />
                  <Route path="/roadmap" element={<Navigate to="/support" replace />} />
                </Route>

                {/* Driver Mobile PWA — standalone layout, no AppShell */}
                <Route path="/driver-app" element={<DriverAppPage />} />

                {/* Customer Portal — standalone layout, no AppShell (WAYP-169) */}
                <Route path="/customer-portal" element={<CustomerPortalPage />} />

                {/* Carrier Portal — standalone layout, no AppShell (WAYP-63).
                    #1518 — each portal menu has its own URL (`/carrier-portal/:section`)
                    so reload / deep-link lands on the right section; the bare
                    `/carrier-portal` redirects to the default section in-page. */}
                <Route path="/carrier-portal" element={<CarrierPortalPage />} />
                <Route path="/carrier-portal/:section" element={<CarrierPortalPage />} />

                {/* Tracx admin — standalone shell at /admin, super_admin only.
                    Coexists with existing /admin/features, /admin/integrations,
                    /admin/jobs, /admin/gps-devices, /admin/onboard-customer
                    leaf routes inside AuthenticatedLayout (more-specific paths
                    win). */}
                <Route
                  path="/admin"
                  element={
                    <RequireSuperAdmin>
                      <PlatformAdminLayout />
                    </RequireSuperAdmin>
                  }
                >
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<PlatformDashboardPage />} />
                  <Route path="organizations" element={<PlatformOrganizationsPage />} />
                  <Route path="organizations/:id" element={<PlatformOrganizationDetailPage />} />
                  <Route path="banners" element={<PlatformBannersPage />} />
                  <Route path="system-health" element={<PlatformSystemHealthPage />} />
                  <Route path="users" element={<PlatformUsersPage />} />
                  <Route path="audit-logs" element={<PlatformAuditLogPage />} />
                  <Route path="ai-usage" element={<PlatformAiUsagePage />} />
                  <Route path="subscriptions" element={<PlatformSubscriptionsPage />} />
                  <Route path="cost-attribution" element={<PlatformCostAttributionPage />} />
                  <Route path="billing-anomalies" element={<PlatformBillingAnomaliesPage />} />
                  <Route path="org-health" element={<PlatformOrgHealthPage />} />
                  <Route path="feature-adoption" element={<PlatformFeatureAdoptionPage />} />
                  <Route path="feature-flags" element={<PlatformFeatureFlagsPage />} />
                  <Route path="integrations" element={<PlatformIntegrationsPage />} />
                  <Route path="api-clients" element={<PlatformApiClientsPage />} />
                  <Route path="support" element={<PlatformSupportPage />} />
                  <Route path="activity" element={<PlatformActivityStreamPage />} />
                </Route>

                {/* Not Found */}
                <Route path="/404" element={<NotFoundPage />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </Suspense>
            </RootErrorBoundary>
          </SessionExpiryListener>
        </Router>
      </ToastProvider>
    </QueryClientProvider>
  );
}
